import { useState, useEffect, useCallback, useRef, memo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { format, addDays, startOfWeek } from 'date-fns';
import { DndContext, DragOverlay, closestCenter, pointerWithin, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import {
  Truck, Home, ChevronLeft, ChevronRight, Printer, UploadCloud, Plus, Search,
  Settings as SettingsIcon, LayoutGrid, X, Loader2, RefreshCw, CalendarClock, Check, Pencil, Move, GripVertical,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { API } from '../App';
import { getPageZoom } from '../utils/desktopZoomFix';
import { useBranch } from '../context/BranchContext';
import VehicleCard from '../components/VehicleCard';
import UnplannedQueueBar, { UnplannedPillView } from '../components/UnplannedQueueBar';
import PlanOrderModal from '../components/PlanOrderModal';
import PrintVehicleModal from '../components/PrintVehicleModal';
import PrintTransportPages from '../components/transport-new/PrintTransportPages';
import TransportMapPanel from '../components/transport-new/TransportMapPanel';
import VehicleMiniCard from '../components/transport-new/VehicleMiniCard';
import DriverHeaderBar from '../components/transport-new/DriverHeaderBar';
import VehicleTrackingModal from '../components/transport-new/VehicleTrackingModal';
import CompactDateBar from '../components/transport-new/CompactDateBar';
import TransportOrderCard from '../components/transport-new/TransportOrderCard';
import { isEntryFinished, isEntryComplete } from '../components/transport-new/entryStatus';
import TransportSearchBar from '../components/transport-new/TransportSearchBar';
import AddDayVehicleDialog from '../components/transport-new/AddDayVehicleDialog';
import AppHeader from '../components/AppHeader';
import { useV3Shell } from '../v3/V3ShellContext';
import V3PopUpSelect from '../v3/V3PopUpSelect';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';

const ORANGE = '#ee7203';
const getCurrentBranch = () => localStorage.getItem('viewAsBranch') || 'C30';

const collisionDetectionStrategy = (args) => {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) return pointerCollisions;
  return closestCenter(args);
};

// The map is memoised so it does not re-render on every re-order while an entry is being dragged.
const MemoTransportMapPanel = memo(TransportMapPanel);

// Re-ordering inside one run is now committed ONCE, on drop (see handleUnplannedDragEnd). The old behaviour, re-ordering
// the React state on every hover (handleEntryDragOver), re-rendered the whole board many times a second and made the
// dragged card lag the mouse. Set this to true only to bring the old behaviour back.
const LIVE_REORDER_WHILE_DRAGGING = false;

// The page is scaled with CSS zoom on laptops / small desktops (utils/desktopZoomFix.js). Leave this true so the drag
// preview and the cards sliding aside follow the mouse exactly; set it to false only to switch the correction off.
const COMPENSATE_PAGE_ZOOM_FOR_DRAG = true;

const countVehicleEntries = (v) => (v?.entries || []).length + (v?.runs || []).reduce((n, r) => n + (r.entries || []).length, 0);

// Which queued save to send next: the oldest batch first, and within a batch (a move queues both vehicles at once) the
// vehicle that GAINS entries before the vehicle that only LOSES them.
const pickNextSaveTask = (queue, knownCounts) => {
  const lowestBatch = Math.min(...queue.map((t) => t.batchId));
  const batch = queue.filter((t) => t.batchId === lowestBatch);
  // A move tags its two saves: the vehicle that GAINS the entry is 'add', the vehicle that LOSES it is 'remove'.
  // A 'remove' is never sent while an 'add' from the same batch is still waiting, whatever the entry counts say
  // (the counts can be stale, e.g. after the Unplan button).
  const adds = batch.filter((t) => t.moveRole === 'add');
  const candidates = adds.length > 0 ? adds : batch;
  const gain = (t) => t.entryCount - (knownCounts[t.vehicleId] ?? t.entryCount);
  return candidates.reduce((best, t) => (gain(t) > gain(best) ? t : best), candidates[0]);
};

const TransportNewUI = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccessibleBranches, canSwitchBranches, setViewAs } = useBranch();

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dailyLog, setDailyLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [appSettings, setAppSettings] = useState(null);
  const [isDateLocked, setIsDateLocked] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const viewLayoutStorageKey = `transport_view_layout_${user?.id || 'anon'}`;
  const [viewLayout, setViewLayout] = useState(() => localStorage.getItem(viewLayoutStorageKey) || 'map');
  useEffect(() => {
    if (!localStorage.getItem(viewLayoutStorageKey)) setViewLayout('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // UI V3: inside the V3 frame, tell the shell how this page is laid out. In Map View (not Edit mode) the page has its own two
  // full-height side panels, so the V3 top bar and control bar sit over the map only; and both V3 edge bars stay as icon
  // strips (the left one just the Encon sphere) that open over the page, to keep room for the map. Outside V3 this does nothing.
  const v3Shell = useV3Shell();
  const setV3PageLayout = v3Shell ? v3Shell.setPageLayout : null;
  useEffect(() => {
    if (!setV3PageLayout) return undefined;
    setV3PageLayout({ panels: !editMode && viewLayout === 'map', compact: true, toolbar: 'bottom' });
    return () => setV3PageLayout(null);
  }, [setV3PageLayout, editMode, viewLayout]);
  const chooseViewLayout = (layout) => {
    localStorage.setItem(viewLayoutStorageKey, layout);
    setViewLayout(layout);
  };
  const [autoPlannedOrderNumbers, setAutoPlannedOrderNumbers] = useState(new Set());
  const [highlightedEntryId, setHighlightedEntryId] = useState(null);
  const [mapFocus, setMapFocus] = useState(null); // { entryId, nonce } — the entry whose postcode was just clicked
  const [maxLines, setMaxLines] = useState(20);
  const [expandedVehicleIds, setExpandedVehicleIds] = useState(new Set());
  const vehicleListRef = useRef(null);
  const mapRightRailRef = useRef(null);
  const columnsScrollRef = useRef(null);
  const [columnsOverflow, setColumnsOverflow] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [editVehicleData, setEditVehicleData] = useState({ vehicle_registration: '', vehicle_type: 'truck', driver_name: '', driver_phone: '', max_weight: 0, has_moffet: false });
  const [saveQueue, setSaveQueue] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [savingVehicles, setSavingVehicles] = useState(new Set());
  const vehicleVersionsRef = useRef({});
  const knownEntryCountRef = useRef({});          // vehicleId -> entry count the server last confirmed
  const inFlightSaveTaskIdRef = useRef(null);      // the save currently being sent
  const failedSaveBatchesRef = useRef(new Set());  // batches where a save failed (so their "remove" half is skipped)
  const lastLocalActivityRef = useRef(0);          // when this screen last changed or saved anything
  const saveBatchRef = useRef({ id: 0, open: false });
  const saveTaskCounterRef = useRef(0);
  const [unplannedRefreshKey, setUnplannedRefreshKey] = useState(0);
  const [planModalData, setPlanModalData] = useState(null);
  const [activeDragItem, setActiveDragItem] = useState(null);
  const [activeEntryDrag, setActiveEntryDrag] = useState(null);
  const dragSourceRef = useRef(null);
  const dragStartRectRef = useRef(null); // screen rectangle of the card (or queue pill) at the moment its drag started
  const dragSnapshotRef = useRef(null);
  const entryDragActiveRef = useRef(false);
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [globalMoveMode, setGlobalMoveMode] = useState(null);
  const [isMultiMoveMode, setIsMultiMoveMode] = useState(false);
  const [checkedEntries, setCheckedEntries] = useState([]);
  const [checkedUnplannedItems, setCheckedUnplannedItems] = useState([]);
  const toggleUnplannedSelection = (item) => {
    setCheckedUnplannedItems((prev) => (prev.some((p) => p.id === item.id) ? prev.filter((p) => p.id !== item.id) : [...prev, item]));
  };
  const handleMoveUnplannedToRun = async (items, vehicleId, runId) => {
    if (!items || items.length === 0) return;
    try {
      for (const item of items) {
        await api.post(`${API}/transport-planning/plan`, { unplanned_id: item.id, vehicle_id: vehicleId, run_choice: runId, merge_entry_id: null, source_ui: 'new' });
      }
      setCheckedUnplannedItems([]);
      setUnplannedRefreshKey((k) => k + 1);
      await loadDailyLog();
      toast.success(`Planned ${items.length} ${items.length === 1 ? 'order' : 'orders'} from the Unplanned Queue`);
    } catch {
      toast.error('Failed to plan one or more unplanned orders');
    }
  };
  const [stateBeforeLastMove, setStateBeforeLastMove] = useState(null);
  const [moveHistory, setMoveHistory] = useState([]);
  const [pendingMove, setPendingMove] = useState(null);
  const [showMoveTypePopup, setShowMoveTypePopup] = useState(false);
  const [moveTypePopupPos, setMoveTypePopupPos] = useState({ x: 0, y: 0 });
  const [dragOverDate, setDragOverDate] = useState(null); // drag-to-date highlight in the vertical rail
  const [monthPickerOpen, setMonthPickerOpen] = useState(false); // month pill above the floating date bar

  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [showCSVUploadModal, setShowCSVUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [vehiclesToPrint, setVehiclesToPrint] = useState([]);
  const [refreshingAll, setRefreshingAll] = useState(false);
  const [showRefreshModal, setShowRefreshModal] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState({ mileageCalculated: 0, csvFetched: 0, totalVehicles: 0, isComplete: false });
  const [showWeeklyUpdateModal, setShowWeeklyUpdateModal] = useState(false);
  const [weeklyUpdateProgress, setWeeklyUpdateProgress] = useState({
    currentDay: '', currentVehicle: '', processedVehicles: 0, totalVehicles: 0, processedDays: 0, totalDays: 7, isComplete: false,
  });

  const dateStr = format(selectedDate, 'yyyy-MM-dd');
  const branch = getCurrentBranch();
  const vehicles = dailyLog?.vehicles || [];
  const selectedVehicle = vehicles.find((v) => v.id === selectedVehicleId) || null;

  const [vehicleTrackingUrls, setVehicleTrackingUrls] = useState({});
  const [vehicleSamsaraIds, setVehicleSamsaraIds] = useState({});
  const [vehicleSamsaraIdsByReg, setVehicleSamsaraIdsByReg] = useState({});
  const [vehicleRegistry, setVehicleRegistry] = useState([]);
  const [branchBasePostcode, setBranchBasePostcode] = useState(null);
  const [baseLocationOverride, setBaseLocationOverride] = useState(null);
  useEffect(() => {
    const fetchTrackingUrls = () => {
      api.get(`${API}/settings/vehicles/${branch}`).then((res) => {
        const map = {};
        const samsaraMap = {};
        const samsaraMapByReg = {};
        (res.data?.default_vehicles || []).forEach((v) => {
          if (v.tracking_url && v.id) map[v.id] = v.tracking_url;
          if (v.samsara_vehicle_id && v.id) samsaraMap[v.id] = v.samsara_vehicle_id;
          if (v.samsara_vehicle_id && v.vehicle_registration) {
            samsaraMapByReg[v.vehicle_registration.trim().toUpperCase().replace(/\s+/g, '')] = v.samsara_vehicle_id;
          }
        });
        setVehicleTrackingUrls(map);
        setVehicleSamsaraIds(samsaraMap);
        setVehicleSamsaraIdsByReg(samsaraMapByReg);
        setVehicleRegistry(res.data?.default_vehicles || []);
        setBranchBasePostcode(res.data?.base_postcode || null);
        setBaseLocationOverride(res.data?.base_location_override || null);
      }).catch(() => { setVehicleTrackingUrls({}); setVehicleSamsaraIds({}); setVehicleSamsaraIdsByReg({}); setVehicleRegistry([]); });
    };
    fetchTrackingUrls();
    window.addEventListener('focus', fetchTrackingUrls);
    const interval = setInterval(fetchTrackingUrls, 60000);
    return () => { window.removeEventListener('focus', fetchTrackingUrls); clearInterval(interval); };
  }, [branch]);

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const dateMode = dateStr === todayStr ? 'today' : (dateStr < todayStr ? 'past' : 'future');
  const [samsaraLiveById, setSamsaraLiveById] = useState({});
  useEffect(() => {
    if (dateMode !== 'today') return;
    let cancelled = false;
    const poll = () => {
      api.get(`${API}/transport/samsara/live-locations`).then((res) => {
        if (cancelled) return;
        const byId = {};
        (res.data?.locations || []).forEach((l) => { byId[l.samsara_vehicle_id] = l; });
        setSamsaraLiveById(byId);
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [dateMode]);
  const liveVehicleFor = (v) => {
    let samsaraId = v && vehicleSamsaraIds[v.registry_vehicle_id || v.id];
    if (!samsaraId && v?.vehicle_registration) {
      samsaraId = vehicleSamsaraIdsByReg[v.vehicle_registration.trim().toUpperCase().replace(/\s+/g, '')];
    }
    return samsaraId ? samsaraLiveById[samsaraId] || null : null;
  };

  const [atDropEntryIds, setAtDropEntryIds] = useState(new Set());
  useEffect(() => {
    if (dateMode !== 'today') { setAtDropEntryIds(new Set()); return; }
    let cancelled = false;
    const poll = () => {
      api.get(`${API}/transport/samsara/at-drop-status/${dateStr}`).then((res) => {
        if (cancelled) return;
        setAtDropEntryIds(new Set(res.data?.at_drop_entry_ids || []));
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [dateMode, dateStr]);

  const [etaData, setEtaData] = useState({ drop_etas: {}, at_drop_etas: {}, base_etas: {}, next_drop_etas: {} });
  useEffect(() => {
    if (dateMode !== 'today') { setEtaData({ drop_etas: {}, at_drop_etas: {}, base_etas: {}, next_drop_etas: {} }); return; }
    let cancelled = false;
    const poll = () => {
      api.get(`${API}/transport/samsara/eta-estimates/${dateStr}`).then((res) => {
        if (cancelled) return;
        setEtaData({
          drop_etas: res.data?.drop_etas || {},
          at_drop_etas: res.data?.at_drop_etas || {},
          base_etas: res.data?.base_etas || {},
          next_drop_etas: res.data?.next_drop_etas || {},
        });
      }).catch(() => {});
    };
    poll();
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [dateMode, dateStr]);
  const baseEtaFor = (vehicle) => (vehicle && etaData.base_etas[vehicle.id]) || null;
  // The entry's own ETA (en route: arrival estimate + journey progress; on site: time on site) plus, for an entry the
  // vehicle is on site at, `next_drop` = the estimate to reach the following drop on that run.
  const etaFor = (entry) => {
    if (!entry) return null;
    const own = etaData.drop_etas[entry.id] || etaData.at_drop_etas[entry.id] || null;
    const nextDrop = etaData.next_drop_etas?.[entry.id] || null;
    // True while the truck is on site at the stop BEFORE this one: that stop's right-aligned "ETA to next drop" tab sits on
    // top of this entry, so this entry squares off its top-right corner to meet the tab cleanly (see TransportOrderCard).
    const tabAbove = Object.values(etaData.next_drop_etas || {}).some((n) => n && n.next_entry_id === entry.id);
    if (!own && !nextDrop && !tabAbove) return null;
    return { ...(own || {}), ...(nextDrop ? { next_drop: nextDrop } : {}), ...(tabAbove ? { tab_above: true } : {}) };
  };

  // Which stops count as finished lives in transport-new/entryStatus.js (a stock transfer is finished once it has a completed_at).
  const computeLiveStatusMap = (vehicle) => {
    const map = {};
    if (dateMode !== 'today' || !vehicle) return map;
    const hasLiveSignal = !!liveVehicleFor(vehicle);
    const runsList = [vehicle.entries || [], ...(vehicle.runs || []).map((r) => r.entries || [])];
    // Between runs the truck goes back to base, loads, then sets off again. While it is heading back to base or loading,
    // the next run's first stop is NOT "en route" — the slim base bar (see TransportOrderCard) covers that period.
    const basePhaseNow = baseEtaFor(vehicle)?.phase || null;
    const holdForBase = hasLiveSignal && (basePhaseNow === 'returning' || basePhaseNow === 'loading');
    let foundNext = false;
    runsList.forEach((entries) => {
      entries.forEach((entry) => {
        if (isEntryFinished(entry)) return;
        if (atDropEntryIds.has(entry.id)) { map[entry.id] = 'at_drop'; foundNext = true; return; }
        if (!foundNext && hasLiveSignal && !holdForBase) { map[entry.id] = 'en_route'; foundNext = true; }
      });
    });
    // Between runs / at the end of the day: "heading back to base" (and, once everything is done, "back at base") is a slim
    // brown bar under the LAST completed delivery; "loading at base" is a slim green bar above the first stop of the next run.
    if (hasLiveSignal && basePhaseNow) {
      const firstRunWithStopsLeft = runsList.findIndex((entries) => entries.some((e) => !isEntryFinished(e)));
      const lastRunToLookIn = firstRunWithStopsLeft === -1 ? runsList.length - 1 : firstRunWithStopsLeft - 1;
      if (basePhaseNow === 'loading' && firstRunWithStopsLeft >= 0) {
        const firstStopOfNextRun = runsList[firstRunWithStopsLeft].find((e) => !isEntryFinished(e));
        if (firstStopOfNextRun) map[firstStopOfNextRun.id] = 'loading';
      } else if (basePhaseNow === 'returning' || basePhaseNow === 'at_base_done') {
        for (let r = lastRunToLookIn; r >= 0; r -= 1) {
          const lastCompleted = [...runsList[r]].reverse().find((e) => isEntryComplete(e));
          if (lastCompleted) { map[lastCompleted.id] = 'to_base'; break; }
        }
      }
    }
    return map;
  };
  const atDropEntryIdFor = (vehicle) => {
    const map = computeLiveStatusMap(vehicle);
    return Object.keys(map).find((id) => map[id] === 'at_drop') || null;
  };

  const [trackingModal, setTrackingModal] = useState(null);
  const openTrackingModal = (vehicle, url) => {
    let rect = null;
    if (editMode && viewLayout === 'map' && vehicleListRef.current && mapRightRailRef.current) {
      const leftRect = vehicleListRef.current.getBoundingClientRect();
      const rightRect = mapRightRailRef.current.getBoundingClientRect();
      const candidate = {
        top: Math.round(Math.min(leftRect.top, rightRect.top)),
        left: Math.round(leftRect.right),
        right: Math.round(window.innerWidth - rightRect.left),
        bottom: Math.round(window.innerHeight - Math.max(leftRect.bottom, rightRect.bottom)),
      };
      if (candidate.left + candidate.right < window.innerWidth - 120) rect = candidate;
    }
    setTrackingModal({ vehicle, url, rect });
  };

  const canEditTransport = () => !!user && (user.role === 'super_admin' || user.can_edit_transport === true);
  const canEditVehicleDetails = () => !!user && ['super_admin', 'manager'].includes(user.role);

  useEffect(() => {
    api.get(`${API}/settings`).then((res) => setAppSettings(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`${API}/backups/locks/check/${dateStr}`)
      .then((res) => {
        setIsDateLocked(res.data.is_locked || false);
        if (res.data.is_locked && editMode) {
          setEditMode(false);
          toast.warning('This date is locked and cannot be edited');
        }
      })
      .catch(() => setIsDateLocked(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr]);

  const getDefaultVehicles = useCallback(async () => {
    try {
      const res = await api.get(`${API}/settings/vehicles-for-date/${branch}/${dateStr}`);
      if (res.data?.vehicles?.length > 0) {
        return res.data.vehicles.map((v) => ({ ...v, entries: [], total_miles: 0, total_weight: 0 }));
      }
    } catch { /* fall through to defaults below */ }
    return [];
  }, [branch, dateStr]);

  const loadDailyLog = useCallback(async () => {
    if (saveQueue.length > 0 || isSaving) return;
    setLoading(true);
    try {
      api.get(`${API}/transport-planning/auto-planned-order-numbers`)
        .then((res) => setAutoPlannedOrderNumbers(new Set(res.data?.order_numbers || [])))
        .catch(() => {});

      const response = await api.get(`${API}/logs/${dateStr}?view_as_branch=${branch}`);
      (response.data?.vehicles || []).forEach((v) => { vehicleVersionsRef.current[v.id] = v.updated_at || null; knownEntryCountRef.current[v.id] = countVehicleEntries(v); });

      if (response.data?.vehicles?.length > 0) {
        try {
          const costsRes = await api.get(`${API}/costs/historical/${dateStr}`);
          const costsArray = Array.isArray(costsRes.data) ? costsRes.data : [];
          const withCosts = response.data.vehicles.map((v) => {
            const c = costsArray.find((c2) => c2.vehicle_registration === v.vehicle_registration);
            return c ? { ...v, contract_hire: c.contract_hire || 0, maintenance_insurance: c.maintenance_insurance || 0, moffet: c.moffet || 0, cost_per_mile: c.cost_per_mile || 0, driver_cost: c.driver_cost || 0 } : v;
          });
          setDailyLog({ ...response.data, vehicles: withCosts });
        } catch {
          setDailyLog(response.data);
        }
      } else {
        const defaults = await getDefaultVehicles();
        setDailyLog({ date: dateStr, vehicles: defaults, version: 1 });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        const defaults = await getDefaultVehicles();
        setDailyLog({ date: dateStr, vehicles: defaults, version: 1 });
      } else {
        toast.error('Failed to load daily log');
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateStr, branch, getDefaultVehicles]);

  useEffect(() => { loadDailyLog(); }, [dateStr, branch]); // eslint-disable-line react-hooks/exhaustive-deps

  // A postcode pill on a Transport entry (TransportOrderCard.jsx) asks for the Map View on its vehicle: switch to the
  // Map View layout and select that vehicle. Works from View All and from Edit mode alike.
  useEffect(() => {
    const openMapOnVehicle = (event) => {
      const vehicleId = event?.detail?.vehicleId;
      const focusEntryId = event?.detail?.entryId || null;
      if (vehicleId && focusEntryId) setMapFocus({ entryId: focusEntryId, nonce: Date.now() });
      if (!vehicleId) return;
      localStorage.setItem(viewLayoutStorageKey, 'map');
      setViewLayout('map');
      setSelectedVehicleId(vehicleId);
    };
    window.addEventListener('c30-transport-open-map', openMapOnVehicle);
    return () => window.removeEventListener('c30-transport-open-map', openMapOnVehicle);
  }, [viewLayoutStorageKey]);

  // ── Search from the vehicle list (TransportSearchBar) ─────────────────────────────────────────────────────────────
  // Both search buttons end by "showing" an entry with the same animation as Global Header search > Show on Transport:
  // the page marks the entry as highlighted for a few seconds (the board already selects the vehicle that holds a
  // highlighted entry) and the entry's card plays the animation (see TransportOrderCard.jsx).
  const highlightTimerRef = useRef(null);
  const flashEntry = (entryKey) => {
    if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    setHighlightedEntryId(null);
    // a moment later, so the same entry can be shown twice in a row and the card is already on screen
    setTimeout(() => setHighlightedEntryId(entryKey), 60);
    highlightTimerRef.current = setTimeout(() => setHighlightedEntryId(null), 9000);
  };
  const handleSearchPickToday = ({ vehicleId, entryId }) => {
    setSelectedVehicleId(vehicleId);
    flashEntry(entryId);
  };
  const handleSearchPickAll = ({ date, entryKey }) => {
    const target = new Date(`${date}T12:00:00`);
    if (!Number.isNaN(target.getTime())) setSelectedDate(target);
    flashEntry(entryKey);
  };
  useEffect(() => () => { if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current); }, []);

  const liveSyncRef = useRef({});
  liveSyncRef.current = {
    saveQueue, savingVehicles, editingVehicle, globalMoveMode, activeEntryDrag,
    activeDragItem, showAddVehicleModal, showCSVUploadModal, showRefreshModal,
    showWeeklyUpdateModal, isMultiMoveMode,
  };

  // `force` is used straight after a failed save or move: the screen is then showing a layout the server never
  // accepted, so it MUST be replaced with the server's copy, even while a Move is in progress or something on the
  // screen was touched while the request was on its way. (A normal 15-second refresh still waits for those.)
  const syncDailyLogSilent = useCallback(async (force = false) => {
    if (document.hidden) return;
    const syncStartedAt = Date.now();
    const s = liveSyncRef.current;
    if (s.activeEntryDrag || s.activeDragItem) return;
    if (!force && (s.globalMoveMode || s.showAddVehicleModal ||
        s.showCSVUploadModal || s.showRefreshModal || s.showWeeklyUpdateModal || s.isMultiMoveMode)) {
      return;
    }
    try {
      const response = await api.get(`${API}/logs/${dateStr}?view_as_branch=${branch}`);
      const freshVehicles = response.data?.vehicles || [];
      // Something was changed or saved on this screen while this request was on its way: the reply is already out of
      // date, so throw it away. The next refresh (15s) will pick up anything genuinely new.
      if (!force && lastLocalActivityRef.current > syncStartedAt) return;
      if (freshVehicles.length === 0) return;

      let costsArray = [];
      try {
        const costsRes = await api.get(`${API}/costs/historical/${dateStr}`);
        costsArray = Array.isArray(costsRes.data) ? costsRes.data : [];
      } catch { /* keep previously-loaded costs on failure */ }

      setDailyLog((prev) => {
        if (!prev) return prev;
        if (!force && lastLocalActivityRef.current > syncStartedAt) return prev;
        const cur = liveSyncRef.current;
        const protectedIds = new Set([
          ...(cur.saveQueue || []).map((t) => t.vehicleId),
          ...(cur.savingVehicles || []),
          cur.editingVehicle?.id,
        ].filter(Boolean));
        const mergedVehicles = freshVehicles.map((fv) => {
          if (protectedIds.has(fv.id)) {
            const localV = prev.vehicles.find((v) => v.id === fv.id);
            return localV || fv;
          }
          vehicleVersionsRef.current[fv.id] = fv.updated_at || null;
          knownEntryCountRef.current[fv.id] = countVehicleEntries(fv);
          const c = costsArray.find((c2) => c2.vehicle_registration === fv.vehicle_registration);
          return c ? { ...fv, contract_hire: c.contract_hire || 0, maintenance_insurance: c.maintenance_insurance || 0, moffet: c.moffet || 0, cost_per_mile: c.cost_per_mile || 0, driver_cost: c.driver_cost || 0 } : fv;
        });
        return { ...prev, ...response.data, vehicles: mergedVehicles };
      });
    } catch { /* silent — the next poll retries */ }
  }, [dateStr, branch]);

  useEffect(() => {
    const interval = setInterval(() => { syncDailyLogSilent(); }, 15000);
    return () => clearInterval(interval);
  }, [syncDailyLogSilent]);

  useEffect(() => {
    if (vehicles.length === 0) { setSelectedVehicleId(null); return; }
    if (!vehicles.some((v) => v.id === selectedVehicleId)) setSelectedVehicleId(vehicles[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dailyLog]);

  useEffect(() => {
    const dateParam = searchParams.get('date');
    const highlightOrderParam = searchParams.get('highlightOrder');
    if (dateParam) {
      const targetDate = new Date(dateParam);
      if (!isNaN(targetDate.getTime())) setSelectedDate(targetDate);
    }
    if (highlightOrderParam) {
      setHighlightedEntryId(highlightOrderParam);
      setTimeout(() => {
        setSearchParams({});
        setTimeout(() => setHighlightedEntryId(null), 8000);
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (!highlightedEntryId || !dailyLog) return;
    const matches = (e) => e.order_number === highlightedEntryId || e.id === highlightedEntryId || (e.order_numbers || []).includes(highlightedEntryId);
    const found = vehicles.find((v) => (v.entries || []).some(matches) || (v.runs || []).some((r) => (r.entries || []).some(matches)));
    if (found) setSelectedVehicleId(found.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedEntryId, dailyLog]);

  useEffect(() => { setMaxLines(20); setExpandedVehicleIds(new Set()); }, [dateStr, branch]);
  useEffect(() => {
    if (editMode) return;
    const el = vehicleListRef.current;
    if (!el) return;
    if (el.scrollHeight > el.clientHeight + 4 && maxLines > 1) {
      setMaxLines((m) => Math.max(1, m - 1));
    }
  }, [maxLines, vehicles, editMode]);

  useEffect(() => {
    if (!editMode && viewLayout !== 'all') return;
    const el = columnsScrollRef.current;
    if (!el) return;
    const check = () => setColumnsOverflow(el.scrollWidth > el.clientWidth + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [editMode, viewLayout, vehicles.length]);

  // View All (and the Edit columns): each vehicle's board, its background, runs down to the bottom of the page, just above the date picker, even when it has few
  // drops, and grows below that line as drops are added. How far down "the bottom" is depends on where the boards start and on the laptop page zoom, so it is
  // measured from the first board and kept up to date as the window or the page changes.
  const [boardMinHeight, setBoardMinHeight] = useState(null);
  const boardsShowing = editMode || viewLayout === 'all';
  useEffect(() => {
    if (!boardsShowing) return undefined;
    const container = columnsScrollRef.current;
    if (!container) return undefined;
    // The date picker (and the part of it that rises above its bar) plus a little air, in the page's own pixels.
    const clearance = v3Shell ? 92 : 124;
    const measure = () => {
      const zoom = getPageZoom() || 1;
      const firstBoard = container.firstElementChild || container;
      const topOnPage = firstBoard.getBoundingClientRect().top + window.scrollY;
      setBoardMinHeight(Math.max(240, Math.round((window.innerHeight - topOnPage) / zoom - clearance)));
    };
    measure();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (observer) { observer.observe(container); observer.observe(document.body); }
    window.addEventListener('resize', measure);
    return () => { if (observer) observer.disconnect(); window.removeEventListener('resize', measure); };
  }, [boardsShowing, v3Shell, vehicles.length, editMode, viewLayout]);

  const scrollColumns = (dir) => {
    columnsScrollRef.current?.scrollBy({ left: dir * 308, behavior: 'smooth' });
  };

  const openEditVehicleModal = (vehicle) => {
    setEditingVehicle(vehicle);
    setEditVehicleData({
      vehicle_registration: vehicle.vehicle_registration || '', vehicle_type: vehicle.vehicle_type || 'truck',
      driver_name: vehicle.driver_name || '', driver_phone: vehicle.driver_phone || '', max_weight: vehicle.max_weight || 0,
      has_moffet: !!vehicle.has_moffet,
    });
  };
  const saveEditedVehicle = () => {
    if (!editingVehicle) return;
    const updated = { ...editingVehicle, ...editVehicleData };
    updateVehicle(editingVehicle.id, updated);
    queueVehicleSave(editingVehicle.id, updated);
    toast.success('Vehicle details updated');
    setEditingVehicle(null);
  };

  const queueVehicleSave = useCallback((vehicleId, vehicleData, meta = {}) => {
    lastLocalActivityRef.current = Date.now();
    // Everything queued in the same tick (for example both halves of a move) shares one batch id.
    const batch = saveBatchRef.current;
    if (!batch.open) {
      batch.open = true;
      batch.id += 1;
      queueMicrotask(() => { saveBatchRef.current.open = false; });
    }
    const batchId = batch.id;
    saveTaskCounterRef.current += 1;
    const taskId = `save-${saveTaskCounterRef.current}`;
    const entryCount = countVehicleEntries(vehicleData);
    setSaveQueue((prev) => {
      // A newer save REPLACES an older one that has not been sent yet, but never the one being sent right now.
      const pendingForVehicle = prev.find((t) => t.vehicleId === vehicleId && t.taskId !== inFlightSaveTaskIdRef.current);
      const kept = prev.filter((t) => t.vehicleId !== vehicleId || t.taskId === inFlightSaveTaskIdRef.current);
      return [...kept, { taskId, vehicleId, vehicleData, date: dateStr, batchId: pendingForVehicle ? pendingForVehicle.batchId : batchId, entryCount, moveRole: meta.moveRole || (pendingForVehicle ? pendingForVehicle.moveRole : null) || null }];
    });
  }, [dateStr]);

  useEffect(() => {
    if (saveQueue.length === 0 || isSaving) return;
    // Oldest batch first. A move queues BOTH vehicles in one batch: the vehicle that GAINS the entry is saved
    // before the vehicle that only LOSES it, so a failure can never leave the entry on neither vehicle.
    const task = pickNextSaveTask(saveQueue, knownEntryCountRef.current);
    const removeThisTask = () => setSaveQueue((prev) => prev.filter((t) => t.taskId !== task.taskId));
    const registration = task.vehicleData.vehicle_registration;

    // The "add" half of a move failed, so do NOT go on to take the entry off the vehicle it is still on.
    const knownCount = knownEntryCountRef.current[task.vehicleId];
    if (failedSaveBatchesRef.current.has(task.batchId) && (task.moveRole === 'remove' || (knownCount !== undefined && task.entryCount < knownCount))) {
      toast.error(`Move not completed — nothing was taken off ${registration}. Refreshing…`);
      removeThisTask();
      setStateBeforeLastMove(null);
      setIsMultiMoveMode(false);
      setCheckedEntries([]);
      setTimeout(() => syncDailyLogSilent(true), 600);
      return;
    }

    setIsSaving(true);
    inFlightSaveTaskIdRef.current = task.taskId;
    setSavingVehicles((prev) => new Set(prev).add(task.vehicleId));
    (async () => {
      try {
        let attempt = 0;
        for (;;) {
          try {
            const payload = { ...task.vehicleData, _client_version: vehicleVersionsRef.current[task.vehicleId] || null, _save_date: task.date };
            const res = await api.put(`${API}/logs/${task.date}/vehicle/${task.vehicleId}?view_as_branch=${branch}&source_ui=new`, payload);
            if (res.data.version) vehicleVersionsRef.current[task.vehicleId] = res.data.version;
            knownEntryCountRef.current[task.vehicleId] = task.entryCount;
            break;
          } catch (error) {
            const status = error.response?.status;
            attempt += 1;
            if (status === 403 || status === 409 || attempt >= 2) throw error;
            await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        }
      } catch (error) {
        failedSaveBatchesRef.current.add(task.batchId);
        if (error.response?.status === 403) {
          toast.error('You do not have permission to edit transport data.');
          setEditMode(false);
        } else if (error.response?.status === 409) {
          toast.error(`${registration} was changed by someone else, so your last change to it was NOT saved. The board is refreshing — please make the change again.`);
          setStateBeforeLastMove(null);
          setIsMultiMoveMode(false);
          setCheckedEntries([]);
          setTimeout(() => syncDailyLogSilent(true), 600);
        } else {
          toast.error(`Failed to save vehicle ${registration}. The board is refreshing — please make the change again.`);
          setStateBeforeLastMove(null);
          setIsMultiMoveMode(false);
          setCheckedEntries([]);
          setTimeout(() => syncDailyLogSilent(true), 600);
        }
      } finally {
        inFlightSaveTaskIdRef.current = null;
        lastLocalActivityRef.current = Date.now();
        setSavingVehicles((prev) => { const n = new Set(prev); n.delete(task.vehicleId); return n; });
        removeThisTask();
        setIsSaving(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveQueue, isSaving]);

  const saveVehicle = useCallback((vehicleId) => {
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    if (vehicle) queueVehicleSave(vehicleId, vehicle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles, queueVehicleSave]);

  const updateVehicle = (vehicleId, updatedVehicle) => {
    lastLocalActivityRef.current = Date.now();
    setDailyLog((prev) => ({ ...prev, vehicles: prev.vehicles.map((v) => (v.id === vehicleId ? updatedVehicle : v)) }));
  };

  const updateMultipleVehicles = (updates) => {
    lastLocalActivityRef.current = Date.now();
    setDailyLog((prev) => ({ ...prev, vehicles: prev.vehicles.map((v) => updates.find((u) => u.id === v.id)?.data || v) }));
  };

  const deleteVehicle = async (vehicleId) => {
    const updatedVehicles = vehicles.filter((v) => v.id !== vehicleId);
    setDailyLog((prev) => ({ ...prev, vehicles: updatedVehicles }));
    try {
      await api.put(`${API}/logs/${dateStr}?view_as_branch=${branch}&source_ui=new`, { date: dateStr, vehicles: updatedVehicles });
      toast.success('Vehicle removed and changes saved');
    } catch {
      toast.error('Failed to save vehicle deletion. Please try again.');
    }
  };

  const [draggedVehicleId, setDraggedVehicleId] = useState(null);
  const [dragOverVehicleId, setDragOverVehicleId] = useState(null);
  const reorderVehicles = (draggedId, targetId) => {
    if (!draggedId || draggedId === targetId) return;
    const current = [...vehicles];
    const fromIdx = current.findIndex((v) => v.id === draggedId);
    const toIdx = current.findIndex((v) => v.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = arrayMove(current, fromIdx, toIdx);
    setDailyLog((prev) => ({ ...prev, vehicles: reordered }));
    api.put(`${API}/logs/${dateStr}?view_as_branch=${branch}&source_ui=new`, { date: dateStr, vehicles: reordered })
      .then(() => toast.success('Vehicle order updated'))
      .catch(() => toast.error('Failed to save new vehicle order'));
  };

  const changeDay = (delta) => {
    if (saveQueue.length > 0) { toast.error(`⚠️ ${saveQueue.length} vehicle(s) still saving — please wait...`); return; }
    setSelectedDate((d) => {
      const thisWeekMonday = startOfWeek(d, { weekStartsOn: 1 });
      return delta > 0 ? addDays(thisWeekMonday, 7) : addDays(thisWeekMonday, -3);
    });
  };
  const upcomingDates = (() => {
    const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
    return Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  })();

  const handleUnplannedDragStart = (event) => {
    const data = event.active?.data?.current;
    dragStartRectRef.current = event.active?.rect?.current?.initial || null;
    if (data?.type === 'entry') {
      dragSourceRef.current = { vehicleId: data.vehicleId, entryId: data.entryId, runId: data.runId };
      dragSnapshotRef.current = vehicles;
      entryDragActiveRef.current = true;
      setActiveEntryDrag(data.entry);
      return;
    }
    const item = data?.item;
    if (item) setActiveDragItem(item);
  };
  const handleEntryDragOver = (event) => {
    const { active, over } = event;
    const activeData = active?.data?.current;
    if (activeData?.type !== 'entry' || !over || !LIVE_REORDER_WHILE_DRAGGING) return;
    const overData = over.data?.current;
    const src = dragSourceRef.current;
    if (!src || !overData) return;
    if (overData.vehicleId !== src.vehicleId || overData.runId !== src.runId) return;
    if (overData.type !== 'entry' || overData.entryId === src.entryId) return;

    setDailyLog((prev) => {
      if (!prev) return prev;
      const reorder = (list) => {
        const oldIndex = (list || []).findIndex((e) => e.id === src.entryId);
        const newIndex = (list || []).findIndex((e) => e.id === overData.entryId);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return list;
        return arrayMove(list, oldIndex, newIndex);
      };
      return {
        ...prev,
        vehicles: prev.vehicles.map((v) => {
          if (v.id !== src.vehicleId) return v;
          if (src.runId === 'main') return { ...v, entries: reorder(v.entries) };
          return { ...v, runs: (v.runs || []).map((r) => (r.id === src.runId ? { ...r, entries: reorder(r.entries) } : r)) };
        }),
      };
    });
  };
  const handleUnplannedDragEnd = (event) => {
    const { active, over } = event;
    const activeData = active?.data?.current;

    if (activeData?.type === 'entry') {
      const src = dragSourceRef.current;
      setActiveEntryDrag(null);
      dragSourceRef.current = null;
      const snapshot = dragSnapshotRef.current;
      dragSnapshotRef.current = null;
      setTimeout(() => { entryDragActiveRef.current = false; }, 300);
      if (!src) return;
      const overData = over?.data?.current;
      if (!overData) {
        if (snapshot) setDailyLog((prev) => (prev ? { ...prev, vehicles: snapshot } : prev));
        return;
      }
      const targetVehicleId = overData.vehicleId;
      const targetRunId = overData.runId;
      const targetEntryId = overData.type === 'entry' ? overData.entryId : 'empty';
      const isSameRun = src.vehicleId === targetVehicleId && src.runId === targetRunId;
      if (isSameRun) {
        const v = vehicles.find((vv) => vv.id === targetVehicleId);
        if (!v) return;
        // The sortable list already showed the shuffle while dragging; apply and save it once, here.
        if (overData.type === 'entry' && overData.entryId !== src.entryId) {
          const reorder = (list) => {
            const oldIndex = (list || []).findIndex((e) => e.id === src.entryId);
            const newIndex = (list || []).findIndex((e) => e.id === overData.entryId);
            if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return list;
            return arrayMove(list, oldIndex, newIndex);
          };
          const reordered = src.runId === 'main'
            ? { ...v, entries: reorder(v.entries) }
            : { ...v, runs: (v.runs || []).map((r) => (r.id === src.runId ? { ...r, entries: reorder(r.entries) } : r)) };
          updateVehicle(v.id, reordered);
          queueVehicleSave(v.id, reordered);
        }
      } else {
        executeGlobalMove(targetVehicleId, targetEntryId, targetRunId, src);
      }
      return;
    }

    setActiveDragItem(null);
    if (!over || !active) return;
    const activeId = String(active.id);
    if (!activeId.startsWith('unplanned-')) return;
    const item = active.data?.current?.item;
    const overData = over.data?.current;
    const overId = String(over.id);
    const vehicleId = overData?.vehicleId || (overId.startsWith('vehicle-') ? overId.replace('vehicle-', '') : null);
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    if (!item || !vehicle) return;
    const presetRunId = overData?.runId || null;
    setPlanModalData({ item, vehicle, presetRunId });
  };

  const handleUnplannedDragCancel = () => {
    setActiveEntryDrag(null);
    setActiveDragItem(null);
    dragSourceRef.current = null;
    dragSnapshotRef.current = null;
    setTimeout(() => { entryDragActiveRef.current = false; }, 300);
  };

  const enterGlobalMoveMode = (vehicleId, entryId, runId) => setGlobalMoveMode({ vehicleId, entryId, runId: runId || 'main' });
  const exitGlobalMoveMode = () => setGlobalMoveMode(null);
  const enterMultiMoveMode = () => { setIsMultiMoveMode(true); setMoveHistory([]); };

  const executeGlobalMove = (targetVehicleId, targetEntryId, targetRunId, sourceOverride = null) => {
    const source = sourceOverride || globalMoveMode;
    if (!source) return;
    const { vehicleId: sourceVehicleId, entryId: sourceEntryId, runId: sourceRunId } = source;
    const sourceVehicleOrig = vehicles.find((v) => v.id === sourceVehicleId);
    const targetVehicleOrig = vehicles.find((v) => v.id === targetVehicleId);
    if (!sourceVehicleOrig || !targetVehicleOrig) { toast.error('Vehicle not found'); return; }

    const getList = (veh, runId) => (runId === 'main' ? (veh.entries || []) : ((veh.runs || []).find((r) => r.id === runId)?.entries || []));
    const entryToMove = getList(sourceVehicleOrig, sourceRunId).find((e) => e.id === sourceEntryId);
    if (!entryToMove) { toast.error('Entry not found'); return; }

    const sameVehicle = sourceVehicleId === targetVehicleId;

    // Check the destination BEFORE taking anything off the source: if the run no longer exists the entry would
    // otherwise be removed from where it is and inserted nowhere.
    const targetRunExists = targetRunId === 'main' || (targetVehicleOrig.runs || []).some((r) => r.id === targetRunId);
    if (!targetRunExists) {
      toast.error('That run no longer exists, so nothing was moved.');
      return;
    }

    const cloneVehicle = (veh) => ({
      ...veh,
      entries: [...(veh.entries || [])],
      runs: (veh.runs || []).map((r) => ({ ...r, entries: [...(r.entries || [])] })),
    });

    let workingSource = cloneVehicle(sourceVehicleOrig);
    if (sourceRunId === 'main') {
      workingSource.entries = workingSource.entries.filter((e) => e.id !== sourceEntryId);
    } else {
      workingSource.runs = workingSource.runs.map((r) => (r.id === sourceRunId ? { ...r, entries: r.entries.filter((e) => e.id !== sourceEntryId) } : r));
    }

    let workingTarget = sameVehicle ? workingSource : cloneVehicle(targetVehicleOrig);

    const insertInto = (list) => {
      const arr = [...(list || [])];
      if (targetEntryId === 'empty') { arr.unshift(entryToMove); return arr; }
      const idx = arr.findIndex((e) => e.id === targetEntryId);
      arr.splice(idx >= 0 ? idx : arr.length, 0, entryToMove);
      return arr;
    };
    if (targetRunId === 'main') {
      workingTarget.entries = insertInto(workingTarget.entries);
    } else {
      workingTarget.runs = workingTarget.runs.map((r) => (r.id === targetRunId ? { ...r, entries: insertInto(r.entries) } : r));
    }

    const updates = sameVehicle
      ? [{ id: sourceVehicleId, data: workingTarget }]
      : [{ id: sourceVehicleId, data: workingSource }, { id: targetVehicleId, data: workingTarget }];

    updateMultipleVehicles(updates);
    updates.forEach((u) => queueVehicleSave(u.id, u.data, sameVehicle ? {} : { moveRole: u.id === targetVehicleId ? 'add' : 'remove' }));
    if (!sourceOverride) exitGlobalMoveMode();
  };

  const handleMultiMoveClick = (vehicleId, entryId, runId, mouseEvent) => {
    if (!globalMoveMode) {
      setGlobalMoveMode({ vehicleId, entryId, runId: runId || 'main' });
    } else {
      setPendingMove({ source: globalMoveMode, target: { vehicleId, entryId, runId: runId || 'main' } });
      setMoveTypePopupPos({ x: mouseEvent.clientX, y: mouseEvent.clientY });
      setShowMoveTypePopup(true);
      setGlobalMoveMode(null);
    }
  };

  const executeMultiMove = (isCopy) => {
    if (!pendingMove) return;
    const { source, target } = pendingMove;
    if (isCopy) {
      const sourceVehicle = vehicles.find((v) => v.id === source.vehicleId);
      const entryToProcess = source.runId === 'main'
        ? sourceVehicle?.entries?.find((e) => e.id === source.entryId)
        : sourceVehicle?.runs?.find((r) => r.id === source.runId)?.entries?.find((e) => e.id === source.entryId);
      if (!entryToProcess) return;
      const copiedEntry = { ...entryToProcess, id: `entry_${Date.now()}` };
      const targetVehicle = vehicles.find((v) => v.id === target.vehicleId);
      if (!targetVehicle) return;
      let updatedTargetVehicle = { ...targetVehicle };
      const insertInto = (list) => {
        const arr = [...(list || [])];
        if (target.entryId === 'empty') { arr.unshift(copiedEntry); return arr; }
        const idx = arr.findIndex((e) => e.id === target.entryId);
        arr.splice(idx >= 0 ? idx : 0, 0, copiedEntry);
        return arr;
      };
      if (target.runId === 'main') updatedTargetVehicle.entries = insertInto(targetVehicle.entries);
      else updatedTargetVehicle.runs = targetVehicle.runs.map((run) => run.id === target.runId ? { ...run, entries: insertInto(run.entries) } : run);
      updateVehicle(target.vehicleId, updatedTargetVehicle);
      queueVehicleSave(target.vehicleId, updatedTargetVehicle);
    } else {
      executeGlobalMove(target.vehicleId, target.entryId, target.runId);
    }
    setPendingMove(null);
    setShowMoveTypePopup(false);
  };

  const handleCrossDateMove = async (selectedEntries, targetVehicleId, targetEntryId, targetRunId, targetDate) => {
    try {
      const entriesByDate = new Map();
      selectedEntries.forEach((entry) => {
        if (!entriesByDate.has(entry.sourceDate)) entriesByDate.set(entry.sourceDate, []);
        entriesByDate.get(entry.sourceDate).push(entry);
      });
      const dateLogsMap = new Map();
      for (const [date] of entriesByDate.entries()) {
        const res = await api.get(`${API}/logs/${date}?view_as_branch=${branch}`);
        dateLogsMap.set(date, res.data);
      }
      if (!dateLogsMap.has(targetDate)) {
        try {
          const res = await api.get(`${API}/logs/${targetDate}?view_as_branch=${branch}`);
          dateLogsMap.set(targetDate, res.data);
        } catch (error) {
          if (error.response?.status === 404) {
            dateLogsMap.set(targetDate, { date: targetDate, vehicles: await getDefaultVehicles(), version: 1 });
          } else throw error;
        }
      }
      const fmtDay = (d) => { try { return format(new Date(d), 'd MMM yyyy'); } catch { return d; } };
      const entriesToMove = [];
      let redeliveredCount = 0;
      for (const [date, entries] of entriesByDate.entries()) {
        const dateLog = dateLogsMap.get(date);
        entries.forEach(({ vehicleId, entryId, runId }) => {
          const vehicle = dateLog.vehicles?.find((v) => v.id === vehicleId);
          if (!vehicle) return;
          let entry;
          if (runId === 'main') {
            entry = vehicle.entries?.find((e) => e.id === entryId);
          } else {
            const run = vehicle.runs?.find((r) => r.id === runId);
            entry = run?.entries?.find((e) => e.id === entryId);
          }
          if (!entry) return;
          if (entry.status === 'failed') {
            entry.redelivery_note = `Failed on ${fmtDay(date)}. New delivery created for ${fmtDay(targetDate)}.`;
            redeliveredCount += 1;
            entriesToMove.push({
              ...entry,
              id: `entry_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              status: '',
              redelivery_note: `Redelivery — the original delivery failed on ${fmtDay(date)}.`,
            });
          } else {
            if (runId === 'main') {
              vehicle.entries = vehicle.entries?.filter((e) => e.id !== entryId) || [];
            } else {
              const run = vehicle.runs?.find((r) => r.id === runId);
              if (run) run.entries = run.entries?.filter((e) => e.id !== entryId) || [];
            }
            entriesToMove.push(entry);
          }
        });
        // The source day is NOT saved here any more: it is saved AFTER the destination day (see below).
      }
      const targetLog = dateLogsMap.get(targetDate);
      const targetVehicle = targetLog.vehicles?.find((v) => v.id === targetVehicleId);
      // Nothing has been saved yet at this point, so every "cannot place it" case stops here with NOTHING changed.
      if (!targetVehicle) { toast.error('The destination vehicle was not found on that day, so nothing was moved.'); return; }
      if (targetRunId !== 'main' && !(targetVehicle.runs || []).some((run) => run.id === targetRunId)) {
        toast.error('The destination run does not exist on that day, so nothing was moved.');
        return;
      }
      if (entriesToMove.length === 0) { toast.error('Nothing was found to move, so nothing was changed.'); return; }
      if (targetVehicle) {
        const insertInto = (list) => {
          const arr = [...(list || [])];
          if (targetEntryId === 'empty') { arr.unshift(...entriesToMove); return arr; }
          const idx = arr.findIndex((e) => e.id === targetEntryId);
          // If the line to insert next to is gone, add to the END of the run rather than silently dropping the entries.
          arr.splice(idx >= 0 ? idx : arr.length, 0, ...entriesToMove);
          return arr;
        };
        if (targetRunId === 'main') targetVehicle.entries = insertInto(targetVehicle.entries);
        else targetVehicle.runs = targetVehicle.runs?.map((run) => run.id === targetRunId ? { ...run, entries: insertInto(run.entries) } : run);
        // SAVE ORDER MATTERS: the DESTINATION day is saved FIRST. If that save fails we stop here and nothing has been
        // taken off any source day. Only once the destination is safely saved are the source day(s) saved; if one of
        // THOSE fails, the delivery shows on both days (visible and fixable) instead of on neither.
        await api.put(`${API}/logs/${targetDate}?view_as_branch=${branch}&source_ui=new`, targetLog);
        let sourceSaveFailed = false;
        for (const sourceDate of entriesByDate.keys()) {
          if (sourceDate === targetDate) continue; // the same document as targetLog, already saved above
          try {
            await api.put(`${API}/logs/${sourceDate}?view_as_branch=${branch}&source_ui=new`, dateLogsMap.get(sourceDate));
          } catch {
            sourceSaveFailed = true;
            toast.error(`Moved to ${fmtDay(targetDate)}, but it could NOT be taken off ${fmtDay(sourceDate)} so it now shows on both days. Please remove it from ${fmtDay(sourceDate)} by hand.`);
          }
        }
        if (dateStr === targetDate) { await new Promise((r) => setTimeout(r, 500)); loadDailyLog(); }
        const movedCount = entriesToMove.length - redeliveredCount;
        const parts = [];
        if (movedCount > 0) parts.push(`Moved ${movedCount}`);
        if (redeliveredCount > 0) parts.push(`Duplicated ${redeliveredCount} failed ${redeliveredCount > 1 ? 'deliveries' : 'delivery'} as new (original stays put)`);
        if (!sourceSaveFailed) toast.success(parts.join(' · ') + '!');
      }
    } catch {
      toast.error('Failed to move entries across dates');
    }
  };

  const handleEntryDroppedOnDate = async (dropData, targetDateStr) => {
    setDragOverDate(null);
    if (!dropData || targetDateStr === dropData.sourceDate) return;
    await handleCrossDateMove([dropData], dropData.vehicleId, 'empty', dropData.runId, targetDateStr);
    loadDailyLog();
  };

  const openAddVehicleModal = () => setShowAddVehicleModal(true);
  // Adds ONE vehicle to the day being viewed (see AddDayVehicleDialog). It is only ever written into this day's board,
  // and is flagged day_only so Settings > Vehicles' clean-up of future days leaves it alone.
  const handleAddDayVehicle = (newVehicle) => {
    lastLocalActivityRef.current = Date.now();
    setDailyLog((prev) => ({ ...prev, vehicles: [...(prev?.vehicles || []), newVehicle] }));
    setShowAddVehicleModal(false);
    setSelectedVehicleId(newVehicle.id);
    toast.success(`${newVehicle.vehicle_registration} added to ${format(selectedDate, 'EEEE d MMMM')} only`);
    setTimeout(() => queueVehicleSave(newVehicle.id, newVehicle), 200);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`${API}/upload/contribution-file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`File "${file.name}" uploaded successfully`);
      setShowCSVUploadModal(false);
      await updateWeekCSVData();
    } catch {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const [preparingPrint, setPreparingPrint] = useState(false);
  const handlePrint = (selectedVehicleIds) => {
    setVehiclesToPrint(vehicles.filter((v) => selectedVehicleIds.includes(v.id)));
    setPreparingPrint(true);
    setTimeout(() => {
      window.print();
      setPreparingPrint(false);
      setVehiclesToPrint([]);
    }, 1800);
  };

  const refreshAllUnplanned = async () => {
    setRefreshingAll(true);
    try {
      const res = await api.post(`${API}/transport-planning/refresh-all`);
      toast.success(`Sweep complete — ${res.data.added} added, ${res.data.already_present} already present.`);
      setUnplannedRefreshKey((k) => k + 1);
    } catch {
      toast.error('Failed to refresh');
    } finally {
      setRefreshingAll(false);
    }
  };

  const comprehensiveRefresh = async () => {
    if (!dailyLog?.vehicles?.length) return;
    setShowRefreshModal(true);
    setRefreshProgress({ mileageCalculated: 0, csvFetched: 0, totalVehicles: dailyLog.vehicles.length, isComplete: false });
    try {
      let updatedVehicles = [...dailyLog.vehicles];
      let mileageCount = 0, csvCount = 0;
      for (const vehicle of updatedVehicles) {
        const vIdx = updatedVehicles.findIndex((v) => v.id === vehicle.id);
        let updatedVehicle = { ...vehicle };
        if (vehicle.entries?.length > 0) {
          const basePostcode = appSettings?.base_postcode;
          for (let i = 0; i < updatedVehicle.entries.length; i++) {
            const entry = updatedVehicle.entries[i];
            if (entry.postcode) {
              try {
                const prevPostcode = i > 0 ? updatedVehicle.entries[i - 1].postcode : basePostcode;
                const distanceResponse = await api.get(`${API}/distance`, { params: { from: prevPostcode, to: entry.postcode } });
                updatedVehicle.entries[i].miles = distanceResponse.data.distance;
              } catch { /* keep existing mileage on failure */ }
            }
          }
          mileageCount++;
          setRefreshProgress((prev) => ({ ...prev, mileageCalculated: mileageCount }));
        }
        if (vehicle.entries?.length > 0) {
          for (let i = 0; i < updatedVehicle.entries.length; i++) {
            const entry = updatedVehicle.entries[i];
            if (entry.order_number) {
              try {
                const [contribRes, salesRes] = await Promise.all([
                  api.get(`${API}/upload/contribution-data/${entry.order_number}`).catch(() => ({ data: { contribution: null } })),
                  api.get(`${API}/upload/sales-order-value/${entry.order_number}`).catch(() => ({ data: { sales_order_value: null } })),
                ]);
                if (contribRes.data.contribution !== null) updatedVehicle.entries[i].contribution = contribRes.data.contribution;
                if (salesRes.data.sales_order_value !== null) updatedVehicle.entries[i].sales_order_value = salesRes.data.sales_order_value;
              } catch { /* leave entry's existing values untouched */ }
            }
          }
          csvCount++;
          setRefreshProgress((prev) => ({ ...prev, csvFetched: csvCount }));
        }
        updatedVehicles[vIdx] = updatedVehicle;
      }
      setDailyLog((prev) => ({ ...prev, vehicles: updatedVehicles }));
      updatedVehicles.forEach((v) => queueVehicleSave(v.id, v));
      setRefreshProgress((prev) => ({ ...prev, isComplete: true }));
      setTimeout(() => setShowRefreshModal(false), 2000);
      toast.success('Refresh complete — mileage and CSV data updated for today');
    } catch {
      toast.error('Failed to complete refresh');
      setShowRefreshModal(false);
    }
  };

  const updateWeekCSVData = async () => {
    try {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekDates = Array.from({ length: 7 }, (_, i) => format(addDays(weekStart, i), 'yyyy-MM-dd'));
      setShowWeeklyUpdateModal(true);
      setWeeklyUpdateProgress({ currentDay: '', currentVehicle: '', processedVehicles: 0, totalVehicles: 0, processedDays: 0, totalDays: 7, isComplete: false });
      let processedDays = 0;
      for (const wDate of weekDates) {
        setWeeklyUpdateProgress((prev) => ({ ...prev, currentDay: format(new Date(wDate), 'EEEE, MMM d'), processedDays }));
        try {
          const res = await api.get(`${API}/logs/${wDate}?view_as_branch=${branch}`);
          const dayVehicles = res.data?.vehicles || [];
          if (dayVehicles.length > 0) {
            setWeeklyUpdateProgress((prev) => ({ ...prev, totalVehicles: dayVehicles.length, processedVehicles: 0 }));
            let processedVehicles = 0;
            for (const vehicle of dayVehicles) {
              setWeeklyUpdateProgress((prev) => ({ ...prev, currentVehicle: vehicle.vehicle_registration, processedVehicles }));
              for (const entry of vehicle.entries || []) {
                if (entry.order_number) {
                  try {
                    const [contribRes, salesRes] = await Promise.all([
                      api.get(`${API}/upload/contribution-data/${entry.order_number}`).catch(() => ({ data: { contribution: null } })),
                      api.get(`${API}/upload/sales-order-value/${entry.order_number}`).catch(() => ({ data: { sales_order_value: null } })),
                    ]);
                    if (contribRes.data.contribution !== null) entry.contribution = contribRes.data.contribution;
                    if (salesRes.data.sales_order_value !== null) entry.sales_order_value = salesRes.data.sales_order_value;
                  } catch { /* leave entry untouched */ }
                }
              }
              processedVehicles++;
              setWeeklyUpdateProgress((prev) => ({ ...prev, processedVehicles }));
            }
            for (const vehicle of dayVehicles) {
              try { await api.put(`${API}/logs/${wDate}/vehicle/${vehicle.id}?view_as_branch=${branch}&source_ui=new`, { ...vehicle, _save_date: wDate }); } catch { /* skip */ }
            }
          }
        } catch (error) {
          if (error.response?.status !== 404) console.error(`Error processing date ${wDate}:`, error);
        }
        processedDays++;
        setWeeklyUpdateProgress((prev) => ({ ...prev, processedDays }));
      }
      setWeeklyUpdateProgress((prev) => ({ ...prev, isComplete: true }));
      await loadDailyLog();
      setTimeout(() => setShowWeeklyUpdateModal(false), 2000);
      toast.success('Week CSV data updated successfully!');
    } catch {
      toast.error('Failed to update weekly CSV data');
      setShowWeeklyUpdateModal(false);
    }
  };

  // While an entry is being dragged, the map keeps showing the vehicle it showed when the drag started, so it does not
  // re-render on every re-order; it catches up as soon as the drag ends.
  const openTrackingModalRef = useRef(openTrackingModal);
  openTrackingModalRef.current = openTrackingModal;
  const stableOpenTracking = useCallback((...args) => openTrackingModalRef.current(...args), []);
  const mapVehicleRef = useRef(null);
  const dragInProgress = !!(activeEntryDrag || activeDragItem);
  if (!dragInProgress) mapVehicleRef.current = selectedVehicle;
  const mapVehicle = dragInProgress ? mapVehicleRef.current : selectedVehicle;

  // dnd-kit measures in real screen pixels, but the drag preview is drawn INSIDE the zoomed page (position: fixed), where
  // both its start position and its movement are multiplied by the page zoom. That is what made the dragged card start
  // away from the pointer and then trail behind it. This converts dnd-kit's numbers into the zoomed page's own pixels.
  const zoomCompensationModifier = ({ transform, activeNodeRect }) => {
    const zoom = getPageZoom();
    if (!COMPENSATE_PAGE_ZOOM_FOR_DRAG || zoom === 1) return transform;
    const rect = dragStartRectRef.current || activeNodeRect;
    if (!rect) return { ...transform, x: transform.x / zoom, y: transform.y / zoom };
    return {
      ...transform,
      x: transform.x / zoom + rect.left * (1 / zoom - 1),
      y: transform.y / zoom + rect.top * (1 / zoom - 1),
    };
  };

  const basePostcode = branchBasePostcode || appSettings?.base_postcode || appSettings?.basePostcode || 'B6 7AF';
  const accessibleBranches = canSwitchBranches ? getAccessibleBranches() : [];

  const handleConfirmMove = () => {
    setStateBeforeLastMove(null);
    setIsMultiMoveMode(false);
    setCheckedEntries([]);
    toast.success('Move saved');
  };
  const handleUndoMove = () => {
    if (!stateBeforeLastMove) { toast.warning('Nothing to undo'); return; }
    setDailyLog((prev) => ({ ...prev, vehicles: stateBeforeLastMove }));
    stateBeforeLastMove.forEach((v) => queueVehicleSave(v.id, v));
    setStateBeforeLastMove(null);
    setIsMultiMoveMode(false);
    setCheckedEntries([]);
    toast.success('Move undone');
  };

  const vehicleCardCommonProps = {
    allVehicles: vehicles, onUpdate: updateVehicle, onUpdateMultiple: updateMultipleVehicles, onDelete: deleteVehicle,
    userRole: user?.role, user, canEditVehicleDetails, settings: appSettings, queueVehicleSave, currentDate: dateStr,
    onUnplanned: () => setUnplannedRefreshKey((k) => k + 1), onEntryAdded: () => {}, vehicleIndex: 0,
    globalMoveMode, onEnterMoveMode: enterGlobalMoveMode, onExecuteMove: executeGlobalMove, onExitMoveMode: exitGlobalMoveMode,
    isMultiMoveMode: editMode && isMultiMoveMode, onEnterMultiMoveMode: enterMultiMoveMode, onMultiMoveClick: handleMultiMoveClick,
    checkedEntries: editMode ? checkedEntries : [], setCheckedEntries, selectedDate, onCrossDateMove: handleCrossDateMove,
    checkedUnplannedItems: editMode && isMultiMoveMode ? checkedUnplannedItems : [], onMoveUnplannedToRun: handleMoveUnplannedToRun,
    onBeforeMove: (vehiclesSnapshot) => setStateBeforeLastMove(JSON.parse(JSON.stringify(vehiclesSnapshot))),
    onServiceIssueSaved: () => {}, currentBranch: branch, autoPlannedOrderNumbers, simpleHeader: true, cardStyle: true,
    highlightedEntryId, dragSuppressRef: entryDragActiveRef,
  };

  const handleMoffetChange = async (vehicle, boolVal) => {
    try {
      await api.patch(`${API}/transport-planning/vehicle-moffet-override`, { date: dateStr, vehicle_id: vehicle.id, has_moffet: boolVal });
      updateVehicle(vehicle.id, { ...vehicle, moffet_override: boolVal });
      toast.success(`Moffet ${boolVal ? 'enabled' : 'disabled'} for today`);
    } catch { toast.error('Failed to update Moffet status'); }
  };

  const editModeToggleButton = canEditTransport() ? (
    <button
      data-testid="transport-new-edit-mode-toggle"
      onClick={() => { if (!isDateLocked) setEditMode((v) => !v); else toast.warning('This date is locked'); }}
      className="relative flex items-center h-6 w-[76px] rounded-full shrink-0 transition-colors duration-200"
      style={{ backgroundColor: editMode ? '#16a34a' : '#e2e8f0' }}
      title="Toggle Edit Mode"
    >
      <span className={`absolute text-[9px] font-bold tracking-wide transition-opacity ${editMode ? 'left-2 text-white' : 'right-2 text-slate-500'}`}>
        {editMode ? 'EDIT' : 'VIEW'}
      </span>
      <span
        className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all duration-200"
        style={{ left: editMode ? 'calc(100% - 22px)' : '2px' }}
      />
    </button>
  ) : null;

  const subHeaderLeft = (
    <>
      <button data-testid="transport-new-back-hub-btn" onClick={() => navigate('/hub')} className="p-1.5 rounded-lg hover:bg-white/20 text-white" title="Back to Hub"><Home size={16} /></button>
      <button data-testid="transport-new-print-btn" onClick={() => setShowPrintModal(true)} title="Print" className="p-1.5 rounded-lg hover:bg-white/20 text-white"><Printer size={16} /></button>
      <button
        data-testid="transport-new-change-view-btn"
        hidden={!!v3Shell}
        onClick={() => chooseViewLayout(viewLayout === 'map' ? 'all' : 'map')}
        title={viewLayout === 'map' ? 'Switch to View All' : 'Switch to Map View'}
        className="text-[11px] font-semibold px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 text-white whitespace-nowrap transition-colors"
      >
        {viewLayout === 'map' ? 'View All' : 'Map View'}
      </button>
    </>
  );
  // Month pill fixed just above the floating date bar — shows the selected date's month/year
  // and opens a calendar popover so a specific date can be jumped to directly, rather than only
  // stepping week-by-week via the bar's prev/next arrows.
  const floatingMonthPill = (
    <Popover open={monthPickerOpen} onOpenChange={setMonthPickerOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="transport-new-month-pill"
          className="fixed bottom-[82px] left-1/2 -translate-x-1/2 z-30 px-4 py-1.5 rounded-full shadow-xl border border-white/15 text-white text-xs font-bold tracking-wide hover:bg-white/15 transition-colors pointer-events-auto"
          style={{ background: 'rgba(15,23,42,0.92)', backdropFilter: 'blur(20px)' }}
          title="Jump to a date"
        >
          {format(selectedDate, 'MMMM yyyy')}
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" side="top" className="bg-white w-auto p-0" data-testid="transport-new-month-pill-calendar">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => { if (d) { setSelectedDate(d); setMonthPickerOpen(false); } }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
  const floatingDateBar = (
    <>
      {floatingMonthPill}
      <CompactDateBar
        floating
        dates={upcomingDates}
        selectedDateStr={dateStr}
        onSelectDate={setSelectedDate}
        onPrev={() => changeDay(-1)}
        onNext={() => changeDay(1)}
        isDateLocked={isDateLocked}
        dragOverDate={dragOverDate}
        onDragOverDate={(d) => { if (dragOverDate !== d) setDragOverDate(d); }}
        onDragLeaveDate={(d) => setDragOverDate((prev) => (prev === d ? null : prev))}
        onDropOnDate={(e, dStr) => {
          try {
            const data = JSON.parse(e.dataTransfer.getData('application/json'));
            handleEntryDroppedOnDate(data, dStr);
          } catch { setDragOverDate(null); }
        }}
      />
    </>
  );
  // UI V3 (Transport), inside the V3 frame: the month pill sits at the LEFT of the control bar (along the bottom of the page), the five-day
  // date strip is centred in it, and the Map View / View All button is drawn in the V3 top bar. Same dates, week stepping and
  // drag-an-entry-onto-a-date as the floating bar they replace (the floating bar still shows when V3 is off).
  const v3MonthPill = (
    <Popover open={monthPickerOpen} onOpenChange={setMonthPickerOpen}>
      <PopoverTrigger asChild>
        <button data-testid="transport-new-month-pill" className="v3-month-pill" title="Jump to a date">
          {format(selectedDate, 'MMMM yyyy')}
        </button>
      </PopoverTrigger>
      <PopoverContent align="center" side="top" className="bg-white w-auto p-0" data-testid="transport-new-month-pill-calendar">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={(d) => { if (d) { setSelectedDate(d); setMonthPickerOpen(false); } }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
  const inlineDateControls = (
    <div className="flex items-center min-w-0" data-testid="transport-new-bottom-date-controls">
      <CompactDateBar
        large
        dates={upcomingDates}
        selectedDateStr={dateStr}
        onSelectDate={setSelectedDate}
        onPrev={() => changeDay(-1)}
        onNext={() => changeDay(1)}
        isDateLocked={isDateLocked}
        dragOverDate={dragOverDate}
        onDragOverDate={(d) => { if (dragOverDate !== d) setDragOverDate(d); }}
        onDragLeaveDate={(d) => setDragOverDate((prev) => (prev === d ? null : prev))}
        onDropOnDate={(e, dStr) => {
          try {
            const data = JSON.parse(e.dataTransfer.getData('application/json'));
            handleEntryDroppedOnDate(data, dStr);
          } catch { setDragOverDate(null); }
        }}
      />
    </div>
  );
  // The Map View / View All button, drawn in the V3 top bar between the search and the theme button. The original button in the control
  // bar is hidden inside V3 (see its `hidden` attribute below) and still shows everywhere else.
  const v3ViewToggle = (v3Shell && v3Shell.topBarSlot) ? createPortal(
    <button
      type="button"
      data-testid="transport-new-change-view-btn-top"
      onClick={() => chooseViewLayout(viewLayout === 'map' ? 'all' : 'map')}
      title={viewLayout === 'map' ? 'Switch to View All' : 'Switch to Map View'}
      className="v3-topbar-pill"
    >
      {viewLayout === 'map' ? 'View All' : 'Map View'}
    </button>,
    v3Shell.topBarSlot,
  ) : null;
  const subHeaderRight = (
    <>
      {editMode && (
        <button
          data-testid="transport-new-quick-move-btn"
          onClick={() => {
            if (isMultiMoveMode) { setIsMultiMoveMode(false); setCheckedEntries([]); setCheckedUnplannedItems([]); }
            else enterMultiMoveMode();
          }}
          className="h-6 px-2.5 rounded-full text-[11px] font-bold flex items-center gap-1 hover:shadow-md transition-all"
          style={isMultiMoveMode ? { backgroundColor: '#16a34a', color: 'white' } : { backgroundColor: 'rgba(255,255,255,0.9)', color: ORANGE }}
          title="Select entries (from runs or Unplanned) to move"
        >
          <Move size={12} /> Move
        </button>
      )}
      {editMode && (
        <button
          data-testid="transport-new-add-vehicle-btn"
          onClick={openAddVehicleModal}
          className="h-6 px-2.5 rounded-full text-[11px] font-bold flex items-center gap-1 hover:shadow-md transition-all"
          style={{ backgroundColor: 'rgba(255,255,255,0.9)', color: ORANGE }}
          title="Add a vehicle to this day only"
        >
          <Plus size={12} /> Vehicle
        </button>
      )}
      {accessibleBranches.length > 1 && v3Shell && (
        <V3PopUpSelect
          value={branch}
          options={accessibleBranches.map((b) => ({ value: b.id, label: b.id }))}
          onChange={(v) => setViewAs(v)}
          testId="transport-new-branch-select"
          ariaLabel="Branch"
        />
      )}
      {accessibleBranches.length > 1 && !v3Shell && (
        <Select value={branch} onValueChange={(v) => setViewAs(v)}>
          <SelectTrigger data-testid="transport-new-branch-select" className="h-6 text-xs w-[58px] px-2 rounded-full bg-white/90 border-0 font-semibold" style={{ color: ORANGE }}><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            {accessibleBranches.map((b) => <SelectItem key={b.id} value={b.id}>{b.id}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {editModeToggleButton}
    </>
  );

  return (
    <>
      <AppHeader
        user={user}
        onLogout={onLogout}
        subHeaderLeft={subHeaderLeft}
        subHeaderRight={subHeaderRight}
        leftButtons={v3Shell ? v3MonthPill : null}
        subHeaderCenter={v3Shell ? inlineDateControls : null}
        hideGlobalNotificationBar={true}
      />
      {v3Shell ? null : floatingDateBar}
      {v3ViewToggle}
      <div className={`w-full min-h-screen bg-slate-950 text-white relative overflow-x-hidden ${dragInProgress ? 'transport-dragging' : ''}`} data-testid="transport-new-ui-page">
      {/* While an entry is being dragged, the glass blur over the full-screen map and the cards' animations are switched
          off: they are what makes the dragged card lag behind the mouse on slower PCs. Everything returns on drop. */}
      <style>{`
        .transport-dragging [class*="backdrop-blur"] { -webkit-backdrop-filter: none !important; backdrop-filter: none !important; }
        .transport-dragging [data-entry-id], .transport-dragging [data-entry-wrapper] { transition: none !important; }

        /* White "liquid glass" board around each vehicle's whole day (its header and runs): the same frosted white glass as the
           Global Header's buttons, with black writing. While the Global Header is in DARK mode the board is dark glass with white
           writing instead (the header row marks itself with data-c30-glass="dark"). --tr-pad is the board's padding, so the
           vehicle header can run right out to the board's edges. */
        .c30-tr-board {
          --tr-pad: 8px;
          --tr-ink: #1d1d1b;
          --tr-ink-soft: rgba(29,29,27,0.62);
          --tr-header-bg: linear-gradient(160deg, rgba(255,255,255,0.97) 0%, rgba(255,244,232,0.82) 100%);
          --tr-header-line: rgba(238,114,3,0.35);
          background: rgba(255,255,255,0.10);
          -webkit-backdrop-filter: blur(24px);
          backdrop-filter: blur(24px);
        }
        .c30-tr-board--roomy { --tr-pad: 12px; }
        /* The vehicle header: full width of the board, always the first thing in it, rounded to match the board's top corners. */
        .c30-tr-header {
          order: -1;
          margin: calc(var(--tr-pad, 8px) * -1) calc(var(--tr-pad, 8px) * -1) 0;
          border-top-left-radius: inherit;
          border-top-right-radius: inherit;
          overflow: hidden;
          background: var(--tr-header-bg);
          border-bottom: 1px solid var(--tr-header-line);
          color: var(--tr-ink);
        }
      `}</style>
      <MemoTransportMapPanel
        basePostcode={basePostcode}
        baseLocationOverride={baseLocationOverride}
        vehicle={mapVehicle}
        className="fixed inset-0 z-0"
        trackingUrl={mapVehicle ? vehicleTrackingUrls[mapVehicle.registry_vehicle_id || mapVehicle.id] : undefined}
        onOpenTracking={stableOpenTracking}
        dateMode={dateMode}
        historyDate={dateStr}
        liveVehicle={liveVehicleFor(mapVehicle)}
        atDropEntryId={atDropEntryIdFor(mapVehicle)}
        focusEntryId={mapFocus?.entryId || null}
        focusNonce={mapFocus?.nonce || 0}
        currentBranchId={branch}
        hideLegend
      />
      {/* View All: the full-screen map behind the run boards is dimmed a little so the boards read clearly against it. */}
      {viewLayout === 'all' && <div className="fixed inset-0 z-[5] pointer-events-none" style={{ background: 'rgba(10, 8, 18, 0.38)' }} data-testid="transport-new-map-dim" />}

      <DndContext sensors={dndSensors} collisionDetection={collisionDetectionStrategy} onDragStart={handleUnplannedDragStart} onDragOver={handleEntryDragOver} onDragEnd={handleUnplannedDragEnd} onDragCancel={handleUnplannedDragCancel}>
        <DragOverlay dropAnimation={null} style={{ zIndex: 99999 }} modifiers={[zoomCompensationModifier]}>
          {activeDragItem ? <UnplannedPillView item={activeDragItem} dragging /> : null}
          {activeEntryDrag ? (
            <div className="rounded-xl bg-white shadow-2xl border-2 border-orange-400 px-2.5 py-1.5 text-[13px] font-bold text-slate-900 opacity-95" style={{ width: dragStartRectRef.current ? dragStartRectRef.current.width / getPageZoom() : 220 }}>
              {activeEntryDrag.customer_name || 'Unnamed customer'}
              <div className="text-[11px] font-medium text-slate-500 mt-0.5">{activeEntryDrag.postcode || ''}</div>
            </div>
          ) : null}
        </DragOverlay>

        <div className="relative z-10 flex flex-col md:flex-row min-h-screen pointer-events-none">
          <div className="flex-1 flex flex-col min-h-0 min-w-0">
            {!editMode && viewLayout === 'map' ? (
            <div className="flex-1 flex flex-col pointer-events-none min-h-0">
              <div className="flex flex-1 min-h-0">
                <div ref={vehicleListRef} data-testid="transport-new-vehicle-list" className="w-full md:w-[350px] md:fixed md:left-0 md:top-[var(--c30-modal-top,84px)] md:bottom-0 flex-shrink-0 p-3 flex flex-col gap-2 bg-white/10 backdrop-blur-xl border-r border-white/10 overflow-y-auto pointer-events-auto">
                  <TransportSearchBar
                    vehicles={vehicles}
                    dayLabel={dateMode === 'today' ? 'Today' : 'This day'}
                    onPickToday={handleSearchPickToday}
                    onPickAll={handleSearchPickAll}
                    getLiveStatus={(vehicle, entry) => computeLiveStatusMap(vehicle)[entry.id]}
                  />
                  {loading ? (
                    <div className="text-center text-white/60 text-xs py-6">Loading…</div>
                  ) : vehicles.length === 0 ? (
                    <div className="text-center text-white/50 text-xs py-6">No vehicles for this date/branch.</div>
                  ) : (
                    [...vehicles.filter((v) => !v.off_road), ...vehicles.filter((v) => v.off_road)].map((v) => (
                      <VehicleMiniCard
                        key={v.id} vehicle={v} isSelected={v.id === selectedVehicleId} onClick={() => setSelectedVehicleId(v.id)}
                        moveModeActive={!!globalMoveMode} maxLines={maxLines} expanded={expandedVehicleIds.has(v.id)}
                        onToggleExpand={(id) => setExpandedVehicleIds((prev) => new Set(prev).add(id))}
                      />
                    ))
                  )}
                </div>

                <div className="hidden md:block flex-1 pointer-events-none" />

                <div
                  className="c30-tr-board c30-tr-board--roomy w-full md:w-[330px] lg:w-[350px] xl:w-[390px] 2xl:w-[430px] md:fixed md:right-0 md:top-[var(--c30-modal-top,84px)] md:bottom-0 flex-shrink-0 min-w-0 p-3 flex flex-col gap-2 border-l overflow-x-hidden overflow-y-auto pointer-events-auto"
                  data-testid="transport-new-run-panel"
                >
                  {selectedVehicle ? (
                    <div className="flex flex-col gap-2" data-testid="transport-new-detail-panel">
                      <div className="c30-tr-header flex-shrink-0">
                        <DriverHeaderBar vehicle={selectedVehicle} trackingUrl={vehicleTrackingUrls[selectedVehicle.registry_vehicle_id || selectedVehicle.id]} onOpenTracking={openTrackingModal} baseEta={baseEtaFor(selectedVehicle)} />
                      </div>
                      <VehicleCard
                        {...vehicleCardCommonProps}
                        vehicle={selectedVehicle}
                        autoSave={() => saveVehicle(selectedVehicle.id)}
                        isViewMode
                        isSaving={savingVehicles.has(selectedVehicle.id)}
                        renderEntry={(entry, runId, runNumber, ctx) => (
                          <TransportOrderCard
                            entry={entry} runId={runId} runNumber={runNumber} ctx={ctx}
                            liveStatus={computeLiveStatusMap(selectedVehicle)[entry.id]}
                            eta={etaFor(entry)}
                            baseEta={baseEtaFor(selectedVehicle)}
                            onMoveSingle={(en, rId) => {
                              setCheckedEntries([{ vehicleId: selectedVehicle.id, entryId: en.id, runId: rId || 'main', sourceDate: dateStr }]);
                              setIsMultiMoveMode(true);
                            }}
                          />
                        )}
                      />
                    </div>
                  ) : (
                    !loading && (
                      <div className="bg-white/90 backdrop-blur rounded-2xl p-10 text-center text-slate-500">
                        Select a vehicle from the list to see its run details.
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          ) : !editMode && viewLayout === 'all' ? (
            <div className="flex-1 flex flex-col pointer-events-auto min-h-0 min-w-0" data-testid="transport-new-view-all-mode">
              <div ref={columnsScrollRef} className="flex-1 flex gap-2 px-2.5 pt-3 pb-[124px] min-h-0">
                {vehicles.length === 0 ? (
                  <div className="text-center text-white/50 text-sm py-10 w-full">No vehicles for this date/branch.</div>
                ) : vehicles.map((v) => (
                  <div
                    key={v.id}
                    className="c30-tr-board flex flex-col gap-2 overflow-y-auto rounded-2xl p-2 flex-1 min-w-0"
                    style={boardMinHeight ? { minHeight: boardMinHeight } : undefined}
                    data-testid={`transport-new-view-all-column-${v.id}`}
                  >
                    <div className="c30-tr-header flex-shrink-0">
                      <DriverHeaderBar vehicle={v} trackingUrl={vehicleTrackingUrls[v.registry_vehicle_id || v.id]} onOpenTracking={openTrackingModal} baseEta={baseEtaFor(v)} />
                    </div>
                    <VehicleCard
                      {...vehicleCardCommonProps}
                      vehicle={v}
                      autoSave={() => saveVehicle(v.id)}
                      isViewMode
                      isSaving={savingVehicles.has(v.id)}
                      renderEntry={(entry, runId, runNumber, ctx) => (
                        <TransportOrderCard entry={entry} runId={runId} runNumber={runNumber} ctx={ctx} liveStatus={computeLiveStatusMap(v)[entry.id]} eta={etaFor(entry)} baseEta={baseEtaFor(v)} />
                      )}
                    />
                  </div>
                ))}
              </div>
              {columnsOverflow && (
                <>
                  <button
                    data-testid="transport-new-view-all-scroll-left"
                    onClick={() => scrollColumns(-1)}
                    className="fixed left-4 bottom-6 z-40 w-11 h-11 rounded-full bg-slate-900/90 hover:bg-slate-800 border border-white/20 shadow-2xl flex items-center justify-center text-white pointer-events-auto"
                    title="Slide to previous truck"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    data-testid="transport-new-view-all-scroll-right"
                    onClick={() => scrollColumns(1)}
                    className="fixed right-4 bottom-6 z-40 w-11 h-11 rounded-full bg-slate-900/90 hover:bg-slate-800 border border-white/20 shadow-2xl flex items-center justify-center text-white pointer-events-auto"
                    title="Slide to next truck"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
          ) : (editMode && viewLayout === 'map') ? (
            <div className="flex-1 flex flex-col pointer-events-none min-h-0" data-testid="transport-new-edit-map-mode">
              <div className="sticky top-0 z-20 px-2.5 pt-3 pb-2 flex-shrink-0 pointer-events-auto">
                <div className="rounded-2xl bg-white shadow-lg p-2.5 max-h-[170px] overflow-y-auto" data-testid="transport-new-edit-map-unplanned-bar">
                  {(checkedEntries.length > 0 || checkedUnplannedItems.length > 0) && (
                    <div className="flex items-center justify-between gap-3 mb-2 bg-orange-500 text-white rounded-lg px-3 py-1.5">
                      <span className="text-xs font-semibold">
                        {checkedEntries.length + checkedUnplannedItems.length} selected — click the green arrow on any run to move {(checkedEntries.length + checkedUnplannedItems.length) > 1 ? 'them' : 'it'} there
                      </span>
                      <button onClick={() => { setCheckedEntries([]); setCheckedUnplannedItems([]); setIsMultiMoveMode(false); }} className="p-0.5 hover:bg-white/20 rounded"><X size={14} /></button>
                    </div>
                  )}
                  {checkedEntries.length === 0 && checkedUnplannedItems.length === 0 && stateBeforeLastMove && (
                    <div className="flex items-center justify-between gap-3 mb-2 bg-blue-600 text-white rounded-lg px-3 py-1.5">
                      <span className="text-xs font-semibold">
                        Moved — <strong>Save</strong> keeps this change, <strong>Undo</strong> puts it back exactly where it was.
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button onClick={handleUndoMove} className="text-xs font-bold px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors">Undo</button>
                        <button onClick={handleConfirmMove} className="text-xs font-bold px-3 py-1 rounded-full bg-green-500 hover:bg-green-600 transition-colors">Save</button>
                      </div>
                    </div>
                  )}
                  <UnplannedQueueBar
                    date={dateStr}
                    branch={branch}
                    refreshKey={unplannedRefreshKey}
                    variant="glass"
                    selectMode={editMode && isMultiMoveMode}
                    selectedIds={checkedUnplannedItems.map((i) => i.id)}
                    onToggleSelect={toggleUnplannedSelection}
                  />
                </div>
              </div>

              <div className="flex flex-1 min-h-0">
                <div ref={vehicleListRef} className="w-full md:w-[350px] flex-shrink-0 p-3 flex flex-col gap-2 bg-white/10 backdrop-blur-xl border-r border-white/10 md:max-h-screen overflow-y-auto pointer-events-auto">
                  <TransportSearchBar
                    vehicles={vehicles}
                    dayLabel={dateMode === 'today' ? 'Today' : 'This day'}
                    onPickToday={handleSearchPickToday}
                    onPickAll={handleSearchPickAll}
                    getLiveStatus={(vehicle, entry) => computeLiveStatusMap(vehicle)[entry.id]}
                  />
                  {loading ? (
                    <div className="text-center text-white/60 text-xs py-6">Loading…</div>
                  ) : vehicles.length === 0 ? (
                    <div className="text-center text-white/50 text-xs py-6">No vehicles for this date/branch.</div>
                  ) : (
                    [...vehicles.filter((v) => !v.off_road), ...vehicles.filter((v) => v.off_road)].map((v) => (
                      <VehicleMiniCard
                        key={v.id} vehicle={v} isSelected={v.id === selectedVehicleId} onClick={() => setSelectedVehicleId(v.id)}
                        moveModeActive={!!globalMoveMode} maxLines={maxLines} expanded={expandedVehicleIds.has(v.id)}
                        onToggleExpand={(id) => setExpandedVehicleIds((prev) => new Set(prev).add(id))}
                      />
                    ))
                  )}
                </div>

                <div className="hidden md:block flex-1 pointer-events-none" />

                <div
                  ref={mapRightRailRef}
                  className="c30-tr-board c30-tr-board--roomy w-full md:w-[330px] lg:w-[350px] xl:w-[390px] 2xl:w-[430px] flex-shrink-0 min-w-0 p-3 flex flex-col gap-2 border-l md:max-h-screen overflow-x-hidden overflow-y-auto pointer-events-auto"
                  data-testid="transport-new-edit-map-run-panel"
                >
                  {selectedVehicle ? (
                    <div className="flex flex-col gap-2" data-testid="transport-new-edit-map-detail-panel">
                      <div className="c30-tr-header flex-shrink-0">
                        <DriverHeaderBar vehicle={selectedVehicle} onEdit={openEditVehicleModal} editable onMoffetChange={handleMoffetChange} trackingUrl={vehicleTrackingUrls[selectedVehicle.registry_vehicle_id || selectedVehicle.id]} onOpenTracking={openTrackingModal} baseEta={baseEtaFor(selectedVehicle)} />
                      </div>
                      <VehicleCard
                        {...vehicleCardCommonProps}
                        vehicle={selectedVehicle}
                        autoSave={() => saveVehicle(selectedVehicle.id)}
                        isViewMode={false}
                        isSaving={savingVehicles.has(selectedVehicle.id)}
                        renderEntry={(entry, runId, runNumber, ctx) => (
                          <TransportOrderCard
                            entry={entry} runId={runId} runNumber={runNumber} ctx={ctx}
                            liveStatus={computeLiveStatusMap(selectedVehicle)[entry.id]}
                            eta={etaFor(entry)}
                            baseEta={baseEtaFor(selectedVehicle)}
                            onMoveSingle={(en, rId) => {
                              setCheckedEntries([{ vehicleId: selectedVehicle.id, entryId: en.id, runId: rId || 'main', sourceDate: dateStr }]);
                              setIsMultiMoveMode(true);
                            }}
                          />
                        )}
                      />
                    </div>
                  ) : (
                    !loading && (
                      <div className="bg-white/90 backdrop-blur rounded-2xl p-10 text-center text-slate-500">
                        Select a vehicle from the list to edit its runs.
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col pointer-events-auto min-h-0 min-w-0" data-testid="transport-new-edit-mode-view">
              <div className="sticky top-0 z-20 px-2.5 pt-3 pb-2 flex-shrink-0" data-testid="transport-new-unplanned-bottom-bar-wrap">
                <div className="rounded-2xl bg-white shadow-lg p-2.5 max-h-[170px] overflow-y-auto" data-testid="transport-new-unplanned-bottom-bar">
                  {(checkedEntries.length > 0 || checkedUnplannedItems.length > 0) && (
                    <div className="flex items-center justify-between gap-3 mb-2 bg-orange-500 text-white rounded-lg px-3 py-1.5" data-testid="transport-new-move-selected-bar">
                      <span className="text-xs font-semibold">
                        {checkedEntries.length + checkedUnplannedItems.length} selected — click the green arrow on any run to move {(checkedEntries.length + checkedUnplannedItems.length) > 1 ? 'them' : 'it'} there
                      </span>
                      <button onClick={() => { setCheckedEntries([]); setCheckedUnplannedItems([]); setIsMultiMoveMode(false); }} className="p-0.5 hover:bg-white/20 rounded"><X size={14} /></button>
                    </div>
                  )}
                  {checkedEntries.length === 0 && checkedUnplannedItems.length === 0 && stateBeforeLastMove && (
                    <div className="flex items-center justify-between gap-3 mb-2 bg-blue-600 text-white rounded-lg px-3 py-1.5" data-testid="transport-new-move-confirm-bar">
                      <span className="text-xs font-semibold">
                        Moved — <strong>Save</strong> keeps this change, <strong>Undo</strong> puts it back exactly where it was.
                      </span>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button data-testid="transport-new-move-undo-btn" onClick={handleUndoMove} className="text-xs font-bold px-3 py-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors">Undo</button>
                        <button data-testid="transport-new-move-save-btn" onClick={handleConfirmMove} className="text-xs font-bold px-3 py-1 rounded-full bg-green-500 hover:bg-green-600 transition-colors">Save</button>
                      </div>
                    </div>
                  )}
                  <UnplannedQueueBar
                    date={dateStr}
                    branch={branch}
                    refreshKey={unplannedRefreshKey}
                    variant="glass"
                    selectMode={editMode && isMultiMoveMode}
                    selectedIds={checkedUnplannedItems.map((i) => i.id)}
                    onToggleSelect={toggleUnplannedSelection}
                  />
                </div>
              </div>

              <div ref={columnsScrollRef} className="flex-1 flex gap-2 px-2.5 pb-[124px] min-h-0">
                {vehicles.length === 0 ? (
                  <div className="text-center text-white/50 text-sm py-10 w-full">No vehicles for this date/branch.</div>
                ) : vehicles.map((v) => (
                  <div
                    key={v.id}
                    onDragOver={(e) => { if (draggedVehicleId && draggedVehicleId !== v.id) { e.preventDefault(); setDragOverVehicleId(v.id); } }}
                    onDragLeave={() => setDragOverVehicleId((id) => (id === v.id ? null : id))}
                    onDrop={(e) => { e.preventDefault(); reorderVehicles(draggedVehicleId, v.id); setDraggedVehicleId(null); setDragOverVehicleId(null); }}
                    className={`c30-tr-board flex flex-col gap-2 overflow-y-auto rounded-2xl border p-2 transition-all flex-1 min-w-0 ${
                      dragOverVehicleId === v.id && draggedVehicleId !== v.id ? 'border-orange-400 ring-2 ring-orange-400/60 bg-orange-500/10' : 'border-transparent'
                    } ${draggedVehicleId === v.id ? 'opacity-40' : ''}`}
                    style={boardMinHeight ? { minHeight: boardMinHeight } : undefined}
                    data-testid={`transport-new-vehicle-column-${v.id}`}
                  >
                    <div
                      draggable
                      onDragStart={(e) => { setDraggedVehicleId(v.id); e.dataTransfer.effectAllowed = 'move'; }}
                      onDragEnd={() => { setDraggedVehicleId(null); setDragOverVehicleId(null); }}
                      data-testid={`transport-new-vehicle-drag-handle-${v.id}`}
                      title="Drag to reposition this vehicle"
                      className="flex items-center gap-1 px-2 py-1 rounded-lg bg-black/5 hover:bg-black/10 cursor-grab active:cursor-grabbing text-[10px] font-semibold select-none flex-shrink-0"
                      style={{ color: 'var(--tr-ink-soft)' }}
                    >
                      <GripVertical size={12} />
                      Drag to reorder
                    </div>
                    <div className="c30-tr-header flex-shrink-0">
                      <DriverHeaderBar vehicle={v} onEdit={openEditVehicleModal} editable onMoffetChange={handleMoffetChange} trackingUrl={vehicleTrackingUrls[v.registry_vehicle_id || v.id]} onOpenTracking={openTrackingModal} baseEta={baseEtaFor(v)} />
                    </div>
                    <VehicleCard
                      {...vehicleCardCommonProps}
                      vehicle={v}
                      autoSave={() => saveVehicle(v.id)}
                      isViewMode={false}
                      isSaving={savingVehicles.has(v.id)}
                      renderEntry={(entry, runId, runNumber, ctx) => (
                        <TransportOrderCard
                          entry={entry} runId={runId} runNumber={runNumber} ctx={ctx}
                          liveStatus={computeLiveStatusMap(v)[entry.id]}
                          eta={etaFor(entry)}
                          baseEta={baseEtaFor(v)}
                          onMoveSingle={(en, rId) => {
                            setCheckedEntries([{ vehicleId: v.id, entryId: en.id, runId: rId || 'main', sourceDate: dateStr }]);
                            setIsMultiMoveMode(true);
                          }}
                        />
                      )}
                    />
                  </div>
                ))}
              </div>

              {columnsOverflow && (
                <>
                  <button
                    data-testid="transport-new-columns-scroll-left"
                    onClick={() => scrollColumns(-1)}
                    className="fixed left-4 bottom-6 z-40 w-11 h-11 rounded-full bg-slate-900/90 hover:bg-slate-800 border border-white/20 shadow-2xl flex items-center justify-center text-white pointer-events-auto"
                    title="Slide to previous truck"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    data-testid="transport-new-columns-scroll-right"
                    onClick={() => scrollColumns(1)}
                    className="fixed right-4 bottom-6 z-40 w-11 h-11 rounded-full bg-slate-900/90 hover:bg-slate-800 border border-white/20 shadow-2xl flex items-center justify-center text-white pointer-events-auto"
                    title="Slide to next truck"
                  >
                    <ChevronRight size={20} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
        </div>
      </DndContext>

      <PlanOrderModal
        open={!!planModalData}
        onClose={() => setPlanModalData(null)}
        item={planModalData?.item}
        vehicle={planModalData?.vehicle}
        presetRunId={planModalData?.presetRunId}
        sourceUi="new"
        onPlanned={() => { setUnplannedRefreshKey((k) => k + 1); loadDailyLog(); }}
      />

      <PrintVehicleModal isOpen={showPrintModal} onClose={() => setShowPrintModal(false)} vehicles={vehicles} onPrint={handlePrint} />
      {preparingPrint && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70" data-testid="print-preparing-overlay">
          <div className="bg-white rounded-2xl px-6 py-4 shadow-2xl flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm font-semibold text-slate-800">Preparing {vehiclesToPrint.length} vehicle{vehiclesToPrint.length !== 1 ? 's' : ''} for print…</span>
          </div>
        </div>
      )}
      {vehiclesToPrint.length > 0 && <PrintTransportPages vehicles={vehiclesToPrint} selectedDate={selectedDate} basePostcode={basePostcode} />}

      <Dialog open={!!editingVehicle} onOpenChange={(open) => !open && setEditingVehicle(null)}>
        <DialogContent className="max-w-lg bg-white border-2 border-orange-200" data-testid="transport-new-edit-vehicle-modal">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2" style={{ color: ORANGE }}><Truck className="w-6 h-6" />Edit Vehicle Details</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="col-span-2">
              <Label>Vehicle Registration *</Label>
              <Select
                value={editVehicleData.vehicle_registration || ''}
                onValueChange={(reg) => {
                  const match = vehicleRegistry.find((v) => v.vehicle_registration === reg);
                  setEditVehicleData({
                    ...editVehicleData,
                    vehicle_registration: reg,
                    registry_vehicle_id: match ? match.id : null,
                    ...(match ? {
                      driver_name: match.driver_name || '',
                      driver_phone: match.driver_phone || '',
                      vehicle_type: match.vehicle_type || 'truck',
                      max_weight: match.max_weight || 0,
                      has_moffet: !!match.has_moffet,
                    } : {}),
                  });
                }}
              >
                <SelectTrigger data-testid="transport-new-edit-vehicle-reg-select" className="mt-1 font-bold"><SelectValue placeholder="Select a vehicle from the registry…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72">
                  {vehicleRegistry.filter((v) => (v.vehicle_registration || '').trim() !== '').map((v) => (
                    <SelectItem key={v.id || v.vehicle_registration} value={v.vehicle_registration}>
                      {v.vehicle_registration}{v.driver_name ? ` — ${v.driver_name}` : ''}{v.is_default === false ? ' (non-default)' : ''}
                    </SelectItem>
                  ))}
                  {editVehicleData.vehicle_registration && !vehicleRegistry.some((v) => v.vehicle_registration === editVehicleData.vehicle_registration) && (
                    <SelectItem value={editVehicleData.vehicle_registration}>{editVehicleData.vehicle_registration} (not in registry)</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Swap to any vehicle in the registry (Settings → Vehicles) — e.g. covering for one that's off the road.
                Driver/type/moffet auto-fill from the registry; edit below to override just for today.
              </p>
            </div>
            <div>
              <Label>Vehicle Type</Label>
              <Select value={editVehicleData.vehicle_type} onValueChange={(v) => setEditVehicleData({ ...editVehicleData, vehicle_type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="truck">Truck</SelectItem><SelectItem value="van">Van</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>Driver Name</Label>
              <Input data-testid="transport-new-edit-driver-name-input" value={editVehicleData.driver_name} onChange={(e) => setEditVehicleData({ ...editVehicleData, driver_name: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label>Driver Phone</Label>
              <Input data-testid="transport-new-edit-driver-phone-input" value={editVehicleData.driver_phone} onChange={(e) => setEditVehicleData({ ...editVehicleData, driver_phone: e.target.value })} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Max Weight (Tonnes)</Label>
              <Input type="number" step="0.1" value={editVehicleData.max_weight} onChange={(e) => setEditVehicleData({ ...editVehicleData, max_weight: parseFloat(e.target.value) || 0 })} className="mt-1" />
            </div>
            <div className="col-span-2">
              <Label>Moffet Availability (default)</Label>
              <Select value={editVehicleData.has_moffet ? 'yes' : 'no'} onValueChange={(v) => setEditVehicleData({ ...editVehicleData, has_moffet: v === 'yes' })}>
                <SelectTrigger data-testid="transport-new-edit-vehicle-moffet-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white"><SelectItem value="no">No Moffet</SelectItem><SelectItem value="yes">Has Moffet</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-between items-center pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              data-testid="transport-new-delete-vehicle-btn"
              onClick={() => {
                if (!editingVehicle) return;
                if (window.confirm(`Delete vehicle ${editingVehicle.vehicle_registration || 'this vehicle'} from today's board? All entries on this vehicle will be permanently removed from this day.`)) {
                  deleteVehicle(editingVehicle.id);
                  setEditingVehicle(null);
                }
              }}
              className="bg-red-50 text-red-600 hover:bg-red-100 border-red-300 font-semibold"
            >
              Delete Vehicle
            </Button>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setEditingVehicle(null)}>Cancel</Button>
              <Button data-testid="transport-new-save-edited-vehicle-btn" onClick={saveEditedVehicle} className="text-white font-semibold" style={{ backgroundColor: ORANGE }}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AddDayVehicleDialog
        open={showAddVehicleModal}
        onOpenChange={setShowAddVehicleModal}
        selectedDate={selectedDate}
        registry={vehicleRegistry}
        existingVehicles={vehicles}
        onAdd={handleAddDayVehicle}
      />

      <Dialog open={showCSVUploadModal} onOpenChange={setShowCSVUploadModal}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2" style={{ color: ORANGE }}><UploadCloud className="w-7 h-7" />Upload CSV File</DialogTitle>
            <DialogDescription>Upload a CSV file with contribution data (Order No., Contribution Value, Sales Order Value)</DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50" style={{ borderColor: ORANGE }}>
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <UploadCloud className="w-10 h-10 mb-3" style={{ color: ORANGE }} />
                <p className="mb-2 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                <p className="text-xs text-gray-500">CSV file (MAX. 10MB)</p>
              </div>
              <input data-testid="transport-new-csv-file-input" type="file" accept=".csv" onChange={handleFileUpload} disabled={uploading} className="hidden" />
            </label>
            {uploading && <div className="mt-4 text-center text-sm" style={{ color: ORANGE }}>Uploading…</div>}
          </div>
          <div className="flex justify-end gap-3 pt-4 border-t"><Button variant="outline" onClick={() => setShowCSVUploadModal(false)}>Close</Button></div>
        </DialogContent>
      </Dialog>

      {showRefreshModal && (
        <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-8 shadow-2xl max-w-md text-center border-2" style={{ borderColor: ORANGE }} data-testid="transport-new-refresh-modal">
            {!refreshProgress.isComplete ? (
              <>
                <div className="animate-spin rounded-full h-14 w-14 border-b-4 mx-auto mb-4" style={{ borderColor: ORANGE }} />
                <h2 className="text-xl font-bold text-slate-800 mb-4">Refreshing Today's Data…</h2>
                <div className="space-y-2 text-left text-sm">
                  <div className="flex items-center justify-between"><span className="text-blue-600 font-semibold">Mileage Calculated:</span><span className="font-bold">{refreshProgress.mileageCalculated} / {refreshProgress.totalVehicles}</span></div>
                  <div className="flex items-center justify-between"><span className="text-green-600 font-semibold">CSV Data Fetched:</span><span className="font-bold">{refreshProgress.csvFetched} / {refreshProgress.totalVehicles}</span></div>
                  <div className="mt-3 w-full bg-slate-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all duration-300" style={{ width: `${((refreshProgress.mileageCalculated + refreshProgress.csvFetched) / Math.max(refreshProgress.totalVehicles * 2, 1)) * 100}%`, backgroundColor: ORANGE }} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3"><Check className="w-8 h-8 text-green-600" /></div>
                <h2 className="text-xl font-bold text-green-600 mb-1">Complete!</h2>
                <p className="text-sm text-slate-600">Mileage and CSV data refreshed and saved</p>
              </>
            )}
          </div>
        </div>
      )}

      {showWeeklyUpdateModal && (
        <div className="fixed inset-0 bg-black/60 z-[10000] flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-8 shadow-2xl max-w-md text-center border-2" style={{ borderColor: ORANGE }} data-testid="transport-new-weekly-update-modal">
            {!weeklyUpdateProgress.isComplete ? (
              <>
                <div className="animate-spin rounded-full h-14 w-14 border-b-4 mx-auto mb-4" style={{ borderColor: ORANGE }} />
                <h2 className="text-xl font-bold text-slate-800 mb-4">Updating Week's CSV Data…</h2>
                <div className="space-y-2 text-left text-sm">
                  <div className="flex items-center justify-between"><span className="text-green-600 font-semibold">Current Day:</span><span className="font-bold">{weeklyUpdateProgress.currentDay || 'Loading…'}</span></div>
                  <div className="flex items-center justify-between"><span className="text-green-600 font-semibold">Current Vehicle:</span><span className="font-bold">{weeklyUpdateProgress.currentVehicle || '-'}</span></div>
                  <div className="flex items-center justify-between"><span className="text-green-600 font-semibold">Vehicles Processed:</span><span className="font-bold">{weeklyUpdateProgress.processedVehicles} / {weeklyUpdateProgress.totalVehicles}</span></div>
                  <div className="flex items-center justify-between"><span className="text-green-600 font-semibold">Days Complete:</span><span className="font-bold">{weeklyUpdateProgress.processedDays} / {weeklyUpdateProgress.totalDays}</span></div>
                  <div className="mt-3 w-full bg-slate-200 rounded-full h-2">
                    <div className="h-2 rounded-full transition-all duration-300" style={{ width: `${(weeklyUpdateProgress.processedDays / weeklyUpdateProgress.totalDays) * 100}%`, backgroundColor: ORANGE }} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3"><Check className="w-8 h-8 text-green-600" /></div>
                <h2 className="text-xl font-bold text-green-600 mb-1">Complete!</h2>
                <p className="text-sm text-slate-600">All CSV data updated for the week</p>
              </>
            )}
          </div>
        </div>
      )}

      {showMoveTypePopup && (
        <div className="fixed z-[9999] bg-white rounded-lg shadow-2xl border-2 border-orange-500 p-4" style={{ left: `${moveTypePopupPos.x}px`, top: `${moveTypePopupPos.y}px`, transform: 'translate(-50%, -100%) translateY(-10px)' }}>
          <div className="text-sm font-bold text-gray-700 mb-3">Choose action:</div>
          <div className="flex gap-3">
            <button onClick={() => executeMultiMove(false)} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold shadow-lg">Move</button>
            <button onClick={() => executeMultiMove(true)} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold shadow-lg">Copy</button>
            <button onClick={() => { setShowMoveTypePopup(false); setPendingMove(null); setGlobalMoveMode(null); }} className="px-3 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-semibold"><X size={14} /></button>
          </div>
        </div>
      )}

      {trackingModal && (
        <VehicleTrackingModal
          vehicle={trackingModal.vehicle}
          url={trackingModal.url}
          rect={trackingModal.rect}
          onClose={() => setTrackingModal(null)}
        />
      )}
      </div>
    </>
  );
};

export default TransportNewUI;
